import OrderWarehouseSection from '../modules/orders/sections/OrderWarehouseSection';
import { createOrderTransportActions } from '../modules/orders/orderTransportActions';
import { pendingInvoiceCostLabel } from '../modules/reports/pendingInvoiceCost';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Table, Tag, Button, Modal, Form, Select, Input, InputNumber, Switch, Upload, App, Timeline, Space, Spin, Tooltip, Tabs, Alert, Segmented } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, UploadOutlined, EditOutlined, DeleteOutlined, SaveOutlined, CloseOutlined, FileTextOutlined, TruckOutlined, DollarOutlined, FilePdfOutlined, CheckSquareOutlined, UserOutlined, CalendarOutlined, MailOutlined, LinkOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import dayjs from 'dayjs';
import { AUTH_USER_CHANGED_EVENT, authService } from '../services/auth';
import api from '../services/api';
import { formatCurrencyLocalized } from '../utils/currency';
import { formatLocalizedPercent, formatLocalizedQuantity } from '../utils/numberFormatting';
import { formatUnit } from '../utils/units';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import { buildSafePdfFileName } from '../utils/pdfFileName';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../utils/polishNumberInput';
import type { OrderSettlementCommandResultDto, OrderSettlementDto } from '../dto/orderSettlement';
import { Cost, OrderMarginHistoryEntry, OrderSettlementEntry, getDisplayOrderNumber } from '../modules/orders/types';
import { getApiErrorMessage } from '../utils/apiError';
import { getOrderSettlementErrorMessage } from '../modules/orders/orderSettlementErrorMessages';
import { useFeedback } from '../utils/feedback';
import { usePageHeaderTitleOverride } from '../modules/navigation/pageHeaderTitleOverride';
import OrderNotificationAssignments from '../components/OrderNotificationAssignments';
import OrderCommunications from '../components/OrderCommunications';
import PresenceBanner from '../components/presence/PresenceBanner';
import { usePresence } from '../hooks/usePresence';
import { internalTaskApi, InternalTask } from '../services/internalTaskApi';
import { getAuthFileUrl } from '../utils/fileUrl';
import {
  convertQuoteToOrder,
  downloadOrderPdf,
  fetchOrderMarginExplanation,
  fetchOrderMarginHistory,
  OrderMarginExplanationDto,
  OrderDocumentLanguage,
  updateOrder,
} from '../modules/orders/api/ordersApi';
import {
  fetchOrderSettlement,
  submitLegacyOrderSettlementCommand,
} from '../modules/orders/api/orderSettlementApi';
import {
  refreshOrderFinanceReadModels,
  runOrderFinanceMutation,
} from '../modules/orders/orderFinanceMutation';
import { refreshOrderSettlementCommandReadModels } from '../modules/orders/orderSettlementCommandMutation';
import {
  getCustomerOrderProgressStatuses,
} from '../modules/orders/customerOrderStatuses';
import {
  fetchSupplierOrders,
  fetchSupplierOrdersForCustomerOrder,
  getSupplierOrderDisplayNumber,
  linkCustomerOrder,
  SupplierOrderDto
} from '../modules/orders/api/supplierOrdersApi';
import { formatSupplierOrderQuantity } from '../modules/orders/supplierOrderUnits';
import { buildSupplierOrderLinkPayload } from '../modules/orders/supplierOrderLinkPayload';
import {
  getSupplierOrderLinkItemNames,
  getSupplierOrderLinkQuantityLabels,
} from '../modules/orders/supplierOrderLinkDisplay';
import {
  MAX_COST_AMOUNT,
  deleteOrderCost,
  isManagedTransportCost,
  isManualCustomerOrderCost,
  isOperationalCustomerOrderCost,
  saveOrderCost,
} from '../modules/orders/api/orderCostsApi';
import { fetchOrderTransports } from '../modules/orders/api/orderTransportApi';
import { canManageTransportRoutes, canReadTransportRoutes } from '../modules/transports/transportRouteAccessPolicy';
import { withTransportReturnContext } from '../modules/transports/transportReturnContext';
import type { OrderTransportsDto } from '../dto/transport';
import {
  canChangeCustomerOrderStatus,
  canDeleteCustomerOrderTransportLink,
  canEditCustomerOrderDetails,
  canEditCustomerOrderDocumentFields,
  canEditCustomerOrderProducts,
  canManageCustomerOrderOperations,
  canManageCustomerOrderSupplierLinks,
  canManageOrderNotificationAssignments,
  canCloseCustomerOrderSettlement,
  canEnterSettlementFinancials,
  canSettleCustomerOrder,
} from '../modules/orders/customerOrderAccessPolicy';
import {
  canShowOrderFinancialCompletenessStatus,
  canShowOrderSalePricesIncompleteHint,
} from '../modules/orders/orderFinancialCompletenessVisibility';
import {
  sendCustomerConfirmationEmail,
  previewCustomerConfirmationEmail,
  previewCustomerConfirmationEmailDraft,
  CustomerConfirmationPreview,
  sendCustomerStatusEmail,
  previewCustomerStatusEmail,
  previewCustomerStatusEmailDraft,
  CustomerStatusEmailPreview
} from '../modules/orders/api/orderStatusApi';
import {
  buildOrderDetailUpdateData,
  getOrderDetailFieldNames,
  getOrderDetailInitialValues,
  OrderDetailEditableField,
} from '../modules/orders/orderDetailEditUtils';
import { customerOrderNumberRule } from '../dto/order';
import {
  OrderStatusFormValues,
  useOrderStatusChangeWithConfirmation,
} from '../modules/orders/useOrderStatusChangeWithConfirmation';
import { useOrderDetail } from '../modules/orders/queries/useOrderDetail';
import { OrderProductsSection } from '../modules/orders/sections/OrderProductsSection';
import { promptOrderPdfConversionUnit } from '../modules/orders/orderPdfConversionPrompt';
import { OrderInfoAccordion } from '../modules/orders/sections/OrderInfoAccordion';
import { OrderMarginExplanationSection } from '../modules/orders/sections/OrderMarginExplanationSection';
import { OrderMarginBuildUp } from '../modules/orders/sections/OrderMarginBuildUp';
import SupplierOrderLinkAllocationFields from '../modules/orders/sections/SupplierOrderLinkAllocationFields';
import { buildSupplierOrderAllocationItemOption } from '../modules/orders/supplierOrderAllocationOptions';
import CurrencyAmount from '../components/CurrencyAmount';
import OrderTransportsSection from '../modules/orders/sections/OrderTransportsSection';
import CustomerEmailEditorModal from '../modules/orders/sections/CustomerEmailEditorModal';
import {
  fetchOrderFulfillmentSource,
  type OrderFulfillmentSource,
} from '../modules/orders/api/orderFulfillmentApi';
import { useSupplierOptions } from '../modules/reference-data/queries/useSupplierOptions';
import {
  DETAIL_META_GRID_STYLE,
  getOrderTypeBadgeStyle,
  getOrderTypeHeaderStyle,
  getStatusBadgeStyle,
  OrderWorkflowRail,
  PAGE_SHELL_STYLE,
  renderSectionTitle,
  SECTION_CARD_BODY_STYLE,
  SECTION_CARD_HEAD_STYLE,
  SECTION_CARD_STYLE,
} from '../modules/orders/orderDetailLayout';
import {
  CUSTOMER_ORDER_ACTIVE_STATUS_VALUES,
  getOrderAllowedTransitions,
} from '../modules/orders/orderStatusTransitions';
import OrderStatusTransitionSelect from '../modules/orders/components/OrderStatusTransitionSelect';
import OrderTransportStatusStrip from '../modules/orders/sections/OrderTransportStatusStrip';
import { getOrderMarginTone } from '../modules/orders/orderMarginPresentation';
import { hasRevenueForMarginPercent } from '../modules/orders/orderFinanceDisplay';
import { canCorrectSettlementEntry } from '../modules/orders/settlementCorrectionEligibility';
import {
  resolveSettlementRequestToken,
  type SettlementRequestToken,
} from '../modules/orders/settlementRequestKey';
import './OrderDetail.css';
import { canUseEditingPresence } from '../modules/presence/presenceAccessPolicy';
import { useOrderSettlementReadiness } from '../modules/orders/useOrderSettlementReadiness';
import { OrderSettlementReadyModal } from '../modules/orders/sections/OrderSettlementReadyModal';
import { OrderSettlementCommandResolutionPanel } from '../modules/orders/sections/OrderSettlementCommandResolutionPanel';
import { OrderSettlementCommandStatus } from '../modules/orders/sections/OrderSettlementCommandStatus';

const isPurchaseInvoiceCost = (cost: Pick<Cost, 'category'>) => cost.category === 'PRODUCT_COST';

const buildSupplierSearchPath = (supplierName?: string) => (
  supplierName ? `/app/suppliers?search=${encodeURIComponent(supplierName)}` : '/app/suppliers'
);

const isManualSupplierAllocation = (record: SupplierOrderDto) =>
  Number(record.allocatedCostPLN || 0) > 0
  && record.effectiveAllocatedPurchaseCostPLN === undefined
  && record.effectiveAllocatedLandedCostPLN === undefined;

const getSupplierAllocationTotalCost = (record: SupplierOrderDto) => (
  Number(record.effectiveAllocatedCostPLN || record.allocatedCostPLN || 0)
);

const getCustomerConfirmationIssueMessage = (
  reason: string | null | undefined,
  t: (key: string) => string,
  useFallback = true,
) => {
  switch (reason) {
    case 'NO_CUSTOMER_EMAIL':
      return t('orders:orderDetail.customerHasNoEmail');
    case 'ORDER_NOT_CONFIRMED':
      return t('orders:orderDetail.customerConfirmationRequiresConfirmed');
    case 'EMAIL_NOT_CONFIGURED':
      return t('orders:orderDetail.customerConfirmationEmailNotConfigured');
    case 'EMAIL_SEND_FAILED':
      return t('orders:orderDetail.customerConfirmationError');
    default:
      return reason && useFallback ? t('orders:orderDetail.customerConfirmationBlockedFallback') : '';
  }
};

const getCustomerStatusEmailIssueMessage = (
  reason: string | null | undefined,
  t: (key: string) => string,
  useFallback = true,
) => {
  switch (reason) {
    case 'NO_CUSTOMER_EMAIL':
      return t('orders:orderDetail.customerHasNoEmail');
    case 'STATUS_EMAILS_DISABLED':
      return t('orders:orderDetail.customerStatusEmailDisabled');
    case 'EMAIL_NOT_CONFIGURED':
      return t('orders:orderDetail.customerConfirmationEmailNotConfigured');
    case 'EMAIL_SEND_FAILED':
      return t('orders:orderDetail.customerStatusEmailError');
    default:
      return reason && useFallback ? t('orders:orderDetail.customerConfirmationBlockedFallback') : '';
  }
};



const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation(['orders', 'common', 'tasks', 'wz']);
  const formatOrderMoney = (
    value: number | string | null | undefined,
    currency = 'PLN',
  ) => formatCurrencyLocalized(value, currency, i18n.language);
  const currentUser = authService.getCurrentUser();
  const presenceOthers = usePresence(
    'ORDER',
    canUseEditingPresence(currentUser) ? id || null : null,
  );
  const { modal } = App.useApp();
  const feedback = useFeedback();
  // Operator-facing identity of a cost line for toasts: the category label,
  // narrowed by the operator's own description when there is one. A Cost has no
  // `name` column, and "Koszt dodany pomyślnie" told nobody which one.
  const describeCost = (category?: string | null, description?: string | null): string => {
    const label = category ? t(`orders:costCategories.${category}`) : '';
    const detail = (description || '').trim();
    if (label && detail) return `${label} — ${detail}`;
    return detail || label || t('orders:costCategories.OTHER');
  };
  const { order, loading, error: orderError, refresh: refreshOrder } = useOrderDetail(id);
  // Operator-facing identity of this order, used by every toast so a
  // confirmation never reads as a bare "Zamówienie zaktualizowane pomyślnie".
  const orderDisplayNumber = order ? getDisplayOrderNumber(order) : '';

  usePageHeaderTitleOverride(
    order
      ? t('orders:pageHeader.customer', { number: getDisplayOrderNumber(order) })
      : null,
  );
  const [orderTransports, setOrderTransports] = useState<OrderTransportsDto>({
    transports: [],
    productRealization: [],
  });
  const [transportLoading, setTransportLoading] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState(searchParams.get('tab') === 'transport' ? '2' : '1');
  const [costModalVisible, setCostModalVisible] = useState(false);
  const [editingCost, setEditingCost] = useState<Cost | null>(null);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [statusForm] = Form.useForm();
  const [editingField, setEditingField] = useState<OrderDetailEditableField | null>(null);
  const [editForm] = Form.useForm();
  const [savingField, setSavingField] = useState<OrderDetailEditableField | null>(null);
  const [numberForm] = Form.useForm();
  const [numberModalOpen, setNumberModalOpen] = useState(false);
  const numberFormUseManual = Form.useWatch('useManualNumbering', numberForm);
  const [supplierOrderLinkForm] = Form.useForm();
  const [orderDocumentLanguage, setOrderDocumentLanguage] = useState<OrderDocumentLanguage>(
    i18n.language.startsWith('pl') ? 'pl' : 'en'
  );
  const [orderDocumentDownloading, setOrderDocumentDownloading] = useState(false);
  const [customerEmailModalVisible, setCustomerEmailModalVisible] = useState(false);
  const [customerEmailSending, setCustomerEmailSending] = useState(false);
  const [customerEmailPreview, setCustomerEmailPreview] = useState<CustomerConfirmationPreview | null>(null);
  const [customerEmailDefaultPreview, setCustomerEmailDefaultPreview] = useState<CustomerConfirmationPreview | null>(null);
  const [customerEmailPreviewLoading, setCustomerEmailPreviewLoading] = useState(false);
  const [customerEmailSubject, setCustomerEmailSubject] = useState('');
  const [customerEmailMessage, setCustomerEmailMessage] = useState('');
  const [customerEmailEditing, setCustomerEmailEditing] = useState(false);
  const [statusEmailModalVisible, setStatusEmailModalVisible] = useState(false);
  const [statusEmailSending, setStatusEmailSending] = useState(false);
  const [statusEmailPreview, setStatusEmailPreview] = useState<CustomerStatusEmailPreview | null>(null);
  const [statusEmailDefaultPreview, setStatusEmailDefaultPreview] = useState<CustomerStatusEmailPreview | null>(null);
  const [statusEmailPreviewLoading, setStatusEmailPreviewLoading] = useState(false);
  const [statusEmailSubject, setStatusEmailSubject] = useState('');
  const [statusEmailMessage, setStatusEmailMessage] = useState('');
  const [statusEmailEditing, setStatusEmailEditing] = useState(false);
  const [marginHistory, setMarginHistory] = useState<OrderMarginHistoryEntry[]>([]);
  const [marginExplanation, setMarginExplanation] = useState<OrderMarginExplanationDto | null>(null);
  const [marginExplanationLoading, setMarginExplanationLoading] = useState(false);
  const [linkedSupplierOrders, setLinkedSupplierOrders] = useState<SupplierOrderDto[]>([]);
  const [fulfillmentSource, setFulfillmentSource] = useState<OrderFulfillmentSource | null>(null);
  const [supplierOrderLinkModalVisible, setSupplierOrderLinkModalVisible] = useState(false);
  const [supplierOrderOptions, setSupplierOrderOptions] = useState<SupplierOrderDto[]>([]);
  const [supplierOrderOptionsLoading, setSupplierOrderOptionsLoading] = useState(false);
  const [supplierOrderLinkSubmitting, setSupplierOrderLinkSubmitting] = useState(false);
  const [orderTasks, setOrderTasks] = useState<InternalTask[]>([]);
  const [orderTasksLoading, setOrderTasksLoading] = useState(false);
  const [submittingCost, setSubmittingCost] = useState(false);
  const [partialSettlementModalOpen, setPartialSettlementModalOpen] = useState(false);
  const [settlementDetails, setSettlementDetails] = useState<OrderSettlementDto | null>(null);
  const [correctionTarget, setCorrectionTarget] = useState<OrderSettlementEntry | null>(null);
  const [correctionModalOpen, setCorrectionModalOpen] = useState(false);
  const [settlementSubmitting, setSettlementSubmitting] = useState(false);
  const [incompleteClosureOpen, setIncompleteClosureOpen] = useState(false);
  const [incompleteClosureReason, setIncompleteClosureReason] = useState('');
  const [correctionForm] = Form.useForm();
  const correctionRetryKey = useRef<SettlementRequestToken | null>(null);
  const correctionInFlight = useRef(false);
  const finalSettlementRetryKey = useRef<SettlementRequestToken | null>(null);
  const finalSettlementInFlight = useRef(false);
  const [, setAuthRevision] = useState(0);

  const canEditOrderDetails = canEditCustomerOrderDetails(currentUser) && !order?.commerciallyFrozenAt;
  const canManageOrderOperations = canManageCustomerOrderOperations(currentUser);
  const canViewOrderTransports = canReadTransportRoutes(currentUser);
  const canManageTransportStatus = canManageTransportRoutes(currentUser);
  const canEditCustomerOrderDocuments = canEditCustomerOrderDocumentFields(currentUser) && !order?.commerciallyFrozenAt && (!currentUser?.invoiceWorkflowEnabled || authService.canManageInvoiceImports());
  const canViewCustomerPayments = authService.canViewCustomerPayments();
  const canChangeOrderStatus = canChangeCustomerOrderStatus(currentUser);
  const canManageOrderCosts = authService.canManageOrderCosts();
  const canViewPurchaseDocuments = authService.canViewPurchaseDocuments();
  const canViewSalesDocuments = authService.canViewSalesDocuments();
  const canViewCustomerSalePrices = authService.canViewCustomerSalePrices();
  const canEditSalesDocuments = authService.canEditCustomerSalePrices() && !order?.commerciallyFrozenAt;
  const canViewMargin = authService.canViewMargin();
  const canViewCustomerOrderFallbackCosts = authService.canViewCustomerOrderFallbackCosts();
  const canEditCustomerOrderFallbackCosts = authService.canEditCustomerOrderFallbackCosts() && !order?.commerciallyFrozenAt;
  const canEditTransportCosts = authService.canEditTransportCosts();
  const canManageSupplierOrders = !currentUser?.invoiceWorkflowEnabled && canManageCustomerOrderSupplierLinks(currentUser) && !order?.commerciallyFrozenAt;
  const canViewSupplierOrderFinance = authService.canViewSupplierOrderFinance();
  const canEditSupplierOrderFinance = authService.canEditSupplierOrderFinance();
  const canEditCosts = canEditCustomerOrderFallbackCosts;
  const canEditProducts = canEditCustomerOrderProducts(currentUser) && !order?.commerciallyFrozenAt;
  const canViewCustomerOrderFinancialCosts = canManageOrderCosts || canViewMargin;
  const canViewCustomerOrderCostSection = canViewCustomerOrderFinancialCosts || canViewCustomerOrderFallbackCosts;
  const canViewCustomerOrderSupplierLinks = canManageSupplierOrders;
  const canManageNotificationAssignments = canManageOrderNotificationAssignments(currentUser);
  const canDeleteTransportLink = canDeleteCustomerOrderTransportLink(currentUser);
  const canCreatePartialSettlement = canSettleCustomerOrder(currentUser);
  const canEnterSettlementFinancialValues = canEnterSettlementFinancials(currentUser);
  const canCloseSettlement = canCloseCustomerOrderSettlement(currentUser);
  const {
    readiness: settlementReadiness,
    loading: settlementReadinessLoading,
    error: settlementReadinessError,
    refresh: refreshSettlementReadiness,
  } = useOrderSettlementReadiness(id, canCreatePartialSettlement);
  const settlementCommandVersion = canEnterSettlementFinancialValues
    ? settlementReadiness?.financialPreviewVersion
    : settlementReadiness?.physicalVersion;
  const canSubmitSettlementCommand = Boolean(
    settlementReadiness?.canSubmit
      && settlementReadiness.actions.submit
      && settlementCommandVersion
      && (!canEnterSettlementFinancialValues || !(settlementReadiness.financialBlockedReasons || []).length)
      && settlementReadiness.commandState !== 'ADMIN_REQUIRED'
      && settlementReadiness.commandState !== 'SUPERSEDED',
  );
  const canFinalizeFromResult = Boolean(canCloseSettlement && !order?.isSettled
    && settlementReadiness?.actions.finalize && !canSubmitSettlementCommand);
  const settlementTrancheLabel = settlementReadiness?.lines
    .filter((line) => line.readyQuantity > 0)
    .map((line) => `${line.productName}: ${formatLocalizedQuantity(line.readyQuantity, i18n.language)} ${formatUnit(line.unit, i18n.language)}`)
    .join(' · ') || '';
  const settlementBasisTransportLabel = settlementReadiness?.basisTransports
    .map((transport) => transport.routeNumber)
    .filter(Boolean)
    .join(', ') || '';
  const selectedSupplierOrderId = Form.useWatch('supplierOrderId', supplierOrderLinkForm);
  const {
    submitting: statusSubmitting,
    submitStatusChange,
  } = useOrderStatusChangeWithConfirmation({
    orderId: id,
    onSuccess: async (values, result) => {
      feedback.success(t('orders:orderDetail.messages.statusUpdateSuccess', {
        orderNumber: orderDisplayNumber,
        status: t(`orders:statusOptions.${values.status}`),
      }));
      if (result.operationalWarning) {
        feedback.warning(result.operationalWarning);
      }
      setStatusModalVisible(false);
      statusForm.resetFields();
      await Promise.all([
        refreshOrder(),
        canViewOrderTransports ? refreshOrderTransports() : Promise.resolve(),
        refreshSettlementReadiness(),
      ]);
    },
    onError: () => {
      feedback.error(t('orders:orderDetail.messages.statusUpdateError', {
        orderNumber: orderDisplayNumber,
      }));
    },
  });
  const [accountManagers, setAccountManagers] = useState<Array<{ id: string; name: string; email?: string }>>([]);
  const { options: suppliers } = useSupplierOptions();
  const customerConfirmation = order?.customerConfirmation;
  const canSendCustomerConfirmation = Boolean(canManageOrderOperations && canViewSalesDocuments && customerConfirmation?.canSend);
  const customerConfirmationDisabledReason = getCustomerConfirmationIssueMessage(
    customerConfirmation?.disabledReason,
    t,
  );
  const showCustomerConfirmationBlockedReason = Boolean(
    canManageOrderOperations && canViewSalesDocuments && !canSendCustomerConfirmation && customerConfirmationDisabledReason,
  );
  const customerStatusEmail = order?.customerStatusEmail;
  const canSendCustomerStatusEmail = Boolean(canManageOrderOperations && customerStatusEmail?.canSend);
  const customerStatusEmailDisabledReason = getCustomerStatusEmailIssueMessage(
    customerStatusEmail?.disabledReason,
    t,
  );
  const useManualNumberingValue = Form.useWatch('useManualNumbering', editForm);

  useEffect(() => {
    const handleAuthUserChanged = () => setAuthRevision((revision) => revision + 1);
    window.addEventListener(AUTH_USER_CHANGED_EVENT, handleAuthUserChanged);
    return () => window.removeEventListener(AUTH_USER_CHANGED_EVENT, handleAuthUserChanged);
  }, []);

  useEffect(() => {
    if (canViewOrderTransports) {
      refreshOrderTransports();
    } else {
      setOrderTransports({ transports: [], productRealization: [] });
    }
    fetchMarginHistory();
    fetchMarginExplanationData();
    fetchSettlementDetails();
    void refreshSettlementReadiness();
    fetchLinkedSupplierOrders();
    fetchFulfillmentSourceData();
    fetchOrderTasks();
    if (canEditOrderDetails) {
      fetchAccountManagers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canEditOrderDetails, canManageOrderOperations, canManageSupplierOrders, canViewMargin, canViewOrderTransports, canViewSalesDocuments, refreshSettlementReadiness]);

  useEffect(() => {
    if (!orderError) {
      return;
    }

    feedback.error(t('orders:orderDetail.messages.fetchError'));
  }, [feedback, orderError, t]);


  const fetchMarginHistory = async () => {
    if (!id || !canViewMargin) return;

    try {
      setMarginHistory(await fetchOrderMarginHistory(id));
    } catch (error) {
      console.error('Error fetching margin history:', error);
    }
  };

  const fetchMarginExplanationData = async () => {
    if (!id || !canViewMargin) return;

    setMarginExplanationLoading(true);
    try {
      setMarginExplanation(await fetchOrderMarginExplanation(id));
    } catch (error) {
      console.error('Error fetching margin explanation:', error);
      setMarginExplanation(null);
    } finally {
      setMarginExplanationLoading(false);
    }
  };

  const fetchSettlementDetails = async () => {
    if (!id || !canViewMargin) {
      setSettlementDetails(null);
      return;
    }

    try {
      setSettlementDetails(await fetchOrderSettlement(id));
    } catch (error) {
      console.error('Error fetching settlement details:', error);
      setSettlementDetails(null);
    }
  };

  const refreshOrderFinancials = async () => {
    await refreshOrderFinanceReadModels({
      order: refreshOrder,
      marginHistory: fetchMarginHistory,
      marginExplanation: fetchMarginExplanationData,
      settlementLedger: fetchSettlementDetails,
      settlementReadiness: refreshSettlementReadiness,
    });
  };

  const fetchLinkedSupplierOrders = async () => {
    if (!id || !canManageSupplierOrders) return;

    try {
      setLinkedSupplierOrders(await fetchSupplierOrdersForCustomerOrder(id));
    } catch (error) {
      console.error('Error fetching linked supplier orders:', error);
    }
  };

  const fetchFulfillmentSourceData = async () => {
    if (!id || !canManageOrderOperations || currentUser?.invoiceWorkflowEnabled) {
      setFulfillmentSource(null);
      return;
    }

    try {
      const result = await fetchOrderFulfillmentSource(id);
      setFulfillmentSource(result.source);
    } catch (error) {
      console.error('Error fetching order fulfillment source:', error);
      setFulfillmentSource(null);
    }
  };

  const openSupplierOrderLinkModal = async () => {
    if (!canManageSupplierOrders) return;

    setSupplierOrderLinkModalVisible(true);
    supplierOrderLinkForm.resetFields();
    supplierOrderLinkForm.setFieldValue('allocateByItem', false);
    setSupplierOrderOptionsLoading(true);

    try {
      setSupplierOrderOptions(await fetchSupplierOrders());
    } catch (error) {
      feedback.error(t('orders:supplierOrders.messages.fetchError'));
    } finally {
      setSupplierOrderOptionsLoading(false);
    }
  };

  const closeSupplierOrderLinkModal = () => {
    setSupplierOrderLinkModalVisible(false);
    supplierOrderLinkForm.resetFields();
  };

  const handleLinkSupplierOrder = async (values: any) => {
    if (!id || !canManageSupplierOrders) return;

    setSupplierOrderLinkSubmitting(true);

    try {
      await linkCustomerOrder(values.supplierOrderId, buildSupplierOrderLinkPayload(values, {
        orderId: id,
        canEditSupplierOrderFinance,
      }));
      feedback.success(t('orders:supplierOrders.messages.linkSuccess', {
        customerOrderNumber: orderDisplayNumber,
      }));
      closeSupplierOrderLinkModal();
      await Promise.all([
        fetchLinkedSupplierOrders(),
        fetchFulfillmentSourceData(),
        refreshOrderFinancials(),
      ]);
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:supplierOrders.messages.linkError', undefined, {
        customerOrderNumber: orderDisplayNumber,
      }));
    } finally {
      setSupplierOrderLinkSubmitting(false);
    }
  };

  const fetchOrderTasks = async () => {
    if (!id) return;
    setOrderTasksLoading(true);
    try {
      const tasks = await internalTaskApi.getTasksByOrder(id);
      setOrderTasks(tasks);
    } catch (error) {
      console.error('Error fetching order tasks:', error);
    } finally {
      setOrderTasksLoading(false);
    }
  };

  async function refreshOrderTransports() {
    if (!id) return;
    setTransportLoading(true);
    try {
      setOrderTransports(await fetchOrderTransports(id));
    } catch (error: any) {
      console.error('Error fetching order transports:', error);
    } finally {
      setTransportLoading(false);
    }
  }

  const refreshSettlementCommandReadModels = async () => {
    await refreshOrderSettlementCommandReadModels({
      order: refreshOrder,
      transports: refreshOrderTransports,
      readiness: refreshSettlementReadiness,
      marginHistory: fetchMarginHistory,
      marginExplanation: fetchMarginExplanationData,
      settlementLedger: fetchSettlementDetails,
    });
  };

  const handleSettlementCommandResult = (
    result: OrderSettlementCommandResultDto,
    source: 'SUBMIT' | 'ADMIN_RESOLUTION' = 'SUBMIT',
  ) => {
    if (result.outcome === 'DUPLICATE') {
      feedback.info(t('orders:orderDetail.settlementCommandDuplicate'));
      return;
    }

    if (result.state === 'ADMIN_REQUIRED') {
      feedback.warning(t('orders:orderDetail.settlementCommandAdminRequiredDescription'));
      return;
    }

    if (result.state === 'APPLIED') {
      feedback.success(source === 'ADMIN_RESOLUTION'
        ? t('orders:orderDetail.settlementCommandAppliedByAdmin')
        : t('orders:orderDetail.settlementCommandApplied'));
      return;
    }

    if (result.state === 'SUPERSEDED') {
      feedback.info(t('orders:orderDetail.settlementCommandSuperseded'));
    }
  };

  const fetchAccountManagers = async () => {
    try {
      const response = await api.get('/users/options');
      setAccountManagers(response.data || []);
    } catch (error) {
      console.error('Error fetching account managers:', error);
    }
  };

  const normalizeUploadFileList = (event: any) => {
    if (Array.isArray(event)) {
      return event;
    }

    return event?.fileList || [];
  };

  const openTransportManagement = (cost?: Cost | null) => {
    if (cost?.transportId) {
      navigate(withTransportReturnContext(
        `/app/transports/${encodeURIComponent(cost.transportId)}/edit`,
        id,
      ));
      return;
    }

    setActiveTabKey('2');
  };

  const closeCostModal = () => {
    setCostModalVisible(false);
    setEditingCost(null);
    form.resetFields();
  };

  const openAddCostModal = () => {
    setEditingCost(null);
    form.resetFields();
    form.setFieldsValue({
      currency: order?.currency || 'PLN',
      document: [],
    });
    setCostModalVisible(true);
  };

  const openEditCostModal = (cost: Cost) => {
    if (isManagedTransportCost(cost)) {
      openTransportManagement(cost);
      return;
    }

    // If cost was entered in a foreign currency, show the original amount;
    // otherwise show the stored PLN amount directly.
    const displayAmount = cost.originalAmount != null && cost.originalCurrency && cost.originalCurrency !== 'PLN'
      ? cost.originalAmount
      : cost.amount;

    setEditingCost(cost);
    form.resetFields();
    form.setFieldsValue({
      currency: cost.originalCurrency || 'PLN',
      amount: displayAmount,
      description: cost.description,
      document: [],
    });
    setCostModalVisible(true);
  };

  const handleSubmitCost = async (values: any) => {
    if (!id) return;

    setSubmittingCost(true);

    try {
      await runOrderFinanceMutation(
        () => saveOrderCost({
          orderId: id,
          costId: editingCost?.id,
          category: editingCost?.category,
          values,
        }),
        refreshOrderFinancials,
      );

      const costName = describeCost(values?.category, values?.description);
      feedback.success(
        editingCost
          ? t('orders:orderDetail.messages.costUpdatedSuccess', { costName })
          : t('orders:orderDetail.messages.costAddedSuccess', { costName })
      );
      closeCostModal();
    } catch (error) {
      feedback.error(
        editingCost
          ? t('orders:orderDetail.messages.costUpdateError')
          : t('orders:orderDetail.messages.costAddError')
      );
    } finally {
      setSubmittingCost(false);
    }
  };

  const handleAddCost = async (values: any) => {
    await handleSubmitCost(values);
  };

  const handleDeleteCost = async (cost: Cost) => {
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('orders:orderDetail.deleteCostConfirmTitle'),
        content: t('orders:orderDetail.deleteCostConfirmDescription'),
        okText: t('orders:orderDetail.delete'),
        okType: 'danger',
        cancelText: t('common:cancel'),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) {
      return;
    }

    try {
      const costName = describeCost(cost.category, cost.description);
      await runOrderFinanceMutation(
        () => deleteOrderCost(cost.id),
        refreshOrderFinancials,
      );
      feedback.success(t('orders:orderDetail.messages.costDeletedSuccess', { costName }));
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:orderDetail.messages.costDeleteError', undefined, {
        costName: describeCost(cost.category, cost.description),
      }));
    }
  };

  const handleSendCustomerConfirmation = async () => {
    if (!id) return;
    setCustomerEmailSending(true);
    try {
      const language = orderDocumentLanguage === 'en' ? 'en' : 'pl';
      const response = await sendCustomerConfirmationEmail(id, {
        language,
        ...(customerEmailEditing
          ? { subject: customerEmailSubject, message: customerEmailMessage }
          : {}),
      });
      feedback.success(t('orders:orderDetail.customerConfirmationSentTo', { email: response.recipient }));
      setCustomerEmailModalVisible(false);
      setCustomerEmailEditing(false);
    } catch (error: any) {
      const serverCode = error?.response?.data?.code;
      const issueMessage = getCustomerConfirmationIssueMessage(serverCode, t, false);
      feedback.error(issueMessage || getApiErrorMessage(error, t, 'orders:orderDetail.customerConfirmationError'));
    } finally {
      setCustomerEmailSending(false);
    }
  };

  useEffect(() => {
    if (!customerEmailModalVisible || !id) {
      return;
    }
    let cancelled = false;
    setCustomerEmailPreviewLoading(true);
    setCustomerEmailPreview(null);
    setCustomerEmailDefaultPreview(null);
    setCustomerEmailSubject('');
    setCustomerEmailMessage('');
    setCustomerEmailEditing(false);
    const language = orderDocumentLanguage === 'en' ? 'en' : 'pl';
    previewCustomerConfirmationEmail(id, language)
      .then((preview) => {
        if (!cancelled) {
          setCustomerEmailPreview(preview);
          setCustomerEmailDefaultPreview(preview);
          setCustomerEmailSubject(preview.subject || '');
          setCustomerEmailMessage(preview.message || '');
        }
      })
      .catch(() => {
        if (!cancelled) feedback.error(t('orders:orderDetail.customerConfirmationPreviewError'));
      })
      .finally(() => {
        if (!cancelled) setCustomerEmailPreviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [customerEmailModalVisible, id, feedback, orderDocumentLanguage, t]);

  useEffect(() => {
    if (!customerEmailModalVisible || !customerEmailEditing || !id) {
      return;
    }
    if (!customerEmailSubject.trim() || !customerEmailMessage.trim()) {
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      const language = orderDocumentLanguage === 'en' ? 'en' : 'pl';
      previewCustomerConfirmationEmailDraft(id, {
        language,
        subject: customerEmailSubject,
        message: customerEmailMessage
      })
        .then((preview) => {
          if (!cancelled) setCustomerEmailPreview(preview);
        })
        .catch(() => {
          if (!cancelled) feedback.error(t('orders:orderDetail.customerConfirmationPreviewError'));
        });
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    customerEmailEditing,
    customerEmailMessage,
    customerEmailModalVisible,
    customerEmailSubject,
    id,
    feedback,
    orderDocumentLanguage,
    t
  ]);

  const handleSendCustomerStatusEmail = async () => {
    if (!id) return;
    setStatusEmailSending(true);
    try {
      const response = await sendCustomerStatusEmail(id, {
        ...(statusEmailEditing
          ? { subject: statusEmailSubject, message: statusEmailMessage }
          : {}),
      });
      feedback.success(t('orders:orderDetail.customerStatusEmailSentTo', { email: response.recipient }));
      setStatusEmailModalVisible(false);
      setStatusEmailEditing(false);
    } catch (error: any) {
      const serverCode = error?.response?.data?.code;
      const issueMessage = getCustomerStatusEmailIssueMessage(serverCode, t, false);
      feedback.error(issueMessage || getApiErrorMessage(error, t, 'orders:orderDetail.customerStatusEmailError'));
    } finally {
      setStatusEmailSending(false);
    }
  };

  useEffect(() => {
    if (!statusEmailModalVisible || !id) {
      return;
    }
    let cancelled = false;
    setStatusEmailPreviewLoading(true);
    setStatusEmailPreview(null);
    setStatusEmailDefaultPreview(null);
    setStatusEmailSubject('');
    setStatusEmailMessage('');
    setStatusEmailEditing(false);
    previewCustomerStatusEmail(id)
      .then((preview) => {
        if (!cancelled) {
          setStatusEmailPreview(preview);
          setStatusEmailDefaultPreview(preview);
          setStatusEmailSubject(preview.subject || '');
          setStatusEmailMessage(preview.message || '');
        }
      })
      .catch(() => {
        if (!cancelled) feedback.error(t('orders:orderDetail.customerStatusEmailPreviewError'));
      })
      .finally(() => {
        if (!cancelled) setStatusEmailPreviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [statusEmailModalVisible, id, feedback, t]);

  useEffect(() => {
    if (!statusEmailModalVisible || !statusEmailEditing || !id) {
      return;
    }
    if (!statusEmailSubject.trim() || !statusEmailMessage.trim()) {
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      previewCustomerStatusEmailDraft(id, {
        subject: statusEmailSubject,
        message: statusEmailMessage
      })
        .then((preview) => {
          if (!cancelled) setStatusEmailPreview(preview);
        })
        .catch(() => {
          if (!cancelled) feedback.error(t('orders:orderDetail.customerStatusEmailPreviewError'));
        });
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    id,
    feedback,
    statusEmailEditing,
    statusEmailMessage,
    statusEmailModalVisible,
    statusEmailSubject,
    t
  ]);

  const handleUpdateStatus = async (values: OrderStatusFormValues) => {
    await submitStatusChange(values);
  };

  const handleDownloadOrderDocument = async () => {
    if (!id || !order || !canViewSalesDocuments) return;

    const conversionChoice = await promptOrderPdfConversionUnit(modal, t);

    if (!conversionChoice) return;

    setOrderDocumentDownloading(true);
    try {
      const orderBlob = await downloadOrderPdf(
        id,
        orderDocumentLanguage,
        conversionChoice === 'none' ? undefined : conversionChoice,
      );
      const url = window.URL.createObjectURL(new Blob([orderBlob]));
      const link = document.createElement('a');
      const filePrefix = orderDocumentLanguage === 'pl' ? 'zamowienie' : 'order';
      link.href = url;
      link.setAttribute('download', `${buildSafePdfFileName(filePrefix, getDisplayOrderNumber(order))}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      feedback.success(t('orders:orderDetail.orderDocumentDownloaded', { orderNumber: orderDisplayNumber }));
    } catch (error) {
      feedback.error(t('orders:orderDetail.orderDocumentDownloadFailed', { orderNumber: orderDisplayNumber }));
    } finally {
      setOrderDocumentDownloading(false);
    }
  };

  const { handleUnlinkTransport, handleTransportStatusChange } = createOrderTransportActions({
    id, modal, t, feedback, orderTransports,
    refreshOrderTransports, refreshOrderFinancials, refreshSettlementReadiness,
  });

  const handleConvertQuote = async () => {
    if (!id) return;

    try {
      await convertQuoteToOrder(id);
      feedback.success(t('orders:orderDetail.quoteConverted', { orderNumber: orderDisplayNumber }));
      await refreshOrder();
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:orderDetail.quoteConvertError', undefined, {
        orderNumber: orderDisplayNumber,
      }));
    }
  };


  const openCorrectionModal = (entry: OrderSettlementEntry) => {
    if (!canCorrectSettlementEntry(entry)) return;
    correctionRetryKey.current = null;
    correctionInFlight.current = false;
    correctionForm.resetFields();
    correctionForm.setFieldsValue({
      recognizedAt: dayjs().format('YYYY-MM-DD'),
      revenue: 0,
      totalCost: 0,
      note: '',
    });
    setCorrectionTarget(entry);
    setCorrectionModalOpen(true);
  };

  const submitCorrection = async (values: {
    recognizedAt: string;
    revenue: number;
    totalCost: number;
    note: string;
  }) => {
    if (!id || !canEnterSettlementFinancialValues || !correctionTarget || correctionInFlight.current) return;

    const command = {
      kind: 'CORRECTION' as const,
      recognizedAt: values.recognizedAt,
      revenue: values.revenue,
      totalCost: values.totalCost,
      note: values.note.trim(),
      correctionOfId: correctionTarget.id,
    };
    correctionRetryKey.current = resolveSettlementRequestToken(
      correctionRetryKey.current,
      JSON.stringify(command),
    );
    const requestKey = correctionRetryKey.current.key;

    correctionInFlight.current = true;
    setSettlementSubmitting(true);
    try {
      await runOrderFinanceMutation(() => submitLegacyOrderSettlementCommand(id, {
        ...command,
        requestKey,
      }), refreshOrderFinancials);
      feedback.success(t('orders:orderDetail.correctionSuccess', { orderNumber: orderDisplayNumber }));
      setCorrectionModalOpen(false);
      setCorrectionTarget(null);
      correctionForm.resetFields();
    } catch (error: any) {
      feedback.error(getOrderSettlementErrorMessage(error, t, 'orders:orderDetail.correctionError', {
        orderNumber: orderDisplayNumber,
      }));
    } finally {
      correctionInFlight.current = false;
      setSettlementSubmitting(false);
    }
  };

  const handleSettleOrder = async (recognizedAt: string, requestKey: string) => {
    if (!id) throw new Error('Order id is required');
    await runOrderFinanceMutation(() => submitLegacyOrderSettlementCommand(id, {
      kind: 'FINAL', recognizedAt, requestKey,
    }), refreshOrderFinancials);
    feedback.success(t('orders:orderDetail.settledSuccess', { orderNumber: orderDisplayNumber }));
  };

  const handleSettleWithRemainder = async () => {
    const reason = incompleteClosureReason.trim();
    if (!id || reason.length < 3 || finalSettlementInFlight.current) return;
    const command = {
      recognizedAt: dayjs().format('YYYY-MM-DD'),
      note: t('orders:orderDetail.incompleteClosureLedgerNote'),
      allowIncompleteDelivery: true,
      incompleteDeliveryReason: reason,
    };
    finalSettlementRetryKey.current = resolveSettlementRequestToken(
      finalSettlementRetryKey.current,
      JSON.stringify(command),
    );
    finalSettlementInFlight.current = true;
    setSettlementSubmitting(true);
    try {
      await runOrderFinanceMutation(() => submitLegacyOrderSettlementCommand(id, {
        kind: 'FINAL',
        ...command,
        requestKey: finalSettlementRetryKey.current!.key,
      }), refreshOrderFinancials);
      finalSettlementRetryKey.current = null;
      setIncompleteClosureOpen(false);
      setIncompleteClosureReason('');
      feedback.success(t('orders:orderDetail.incompleteClosureSuccess', { orderNumber: orderDisplayNumber }));
    } catch (error: any) {
      feedback.error(getOrderSettlementErrorMessage(error, t, 'orders:orderDetail.settleError', {
        orderNumber: orderDisplayNumber,
      }));
    } finally {
      finalSettlementInFlight.current = false;
      setSettlementSubmitting(false);
    }
  };

  const startEditing = (field: OrderDetailEditableField) => {
    if (!order) return;

    editForm.resetFields();
    setEditingField(field);
    editForm.setFieldsValue(getOrderDetailInitialValues(order, field));
  };

  const cancelEditing = (field: OrderDetailEditableField) => {
    if (editingField !== field) return;

    setEditingField(null);
    editForm.resetFields();
  };

  const saveField = async (field: OrderDetailEditableField) => {
    if (!id) return;

    try {
      const values = editForm.getFieldsValue(true);
      await editForm.validateFields(getOrderDetailFieldNames(field, values));
      setSavingField(field);
      const updateData = buildOrderDetailUpdateData(field, values);

      await updateOrder(id, updateData);
      feedback.success(t('orders:orderDetail.messages.updateSuccess', {
        orderNumber: orderDisplayNumber,
      }));
      cancelEditing(field);
      await refreshOrder();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      feedback.error(getApiErrorMessage(error, t, 'orders:orderDetail.messages.updateError', undefined, {
        orderNumber: orderDisplayNumber,
      }));
    } finally {
      setSavingField(null);
    }
  };

  const saveOrderNumber = async () => {
    if (!id) return;
    try {
      const values = numberForm.getFieldsValue(true);
      await numberForm.validateFields(getOrderDetailFieldNames('manualNumbering', values));
      setSavingField('manualNumbering');
      await updateOrder(id, buildOrderDetailUpdateData('manualNumbering', values));
      feedback.success(t('orders:orderDetail.messages.updateSuccess', {
        orderNumber: orderDisplayNumber,
      }));
      setNumberModalOpen(false);
      await refreshOrder();
    } catch (error: any) {
      if (error?.errorFields) return;
      feedback.error(getApiErrorMessage(error, t, 'orders:orderDetail.messages.updateError', undefined, {
        orderNumber: orderDisplayNumber,
      }));
    } finally {
      setSavingField(null);
    }
  };

  const getNumberModalInitialValues = (): Record<string, unknown> => {
    if (!order) return {};
    const init = getOrderDetailInitialValues(order, 'manualNumbering');
    if (!order.useManualNumbering) {
      init.manualOrderNumber = getDisplayOrderNumber(order);
    }
    return init;
  };

  const renderEditableField = (
    field: OrderDetailEditableField,
    value: React.ReactNode,
    renderComponent: () => React.ReactNode,
    canEditField = canEditOrderDetails
  ) => {
    if (!canEditField) return value;

    const isEditing = editingField === field;

    if (isEditing) {
      return (
        <div style={{ display: 'grid', gap: 10, width: '100%' }}>
          {renderComponent()}
          <Space wrap>
            <Button
              icon={<SaveOutlined />}
              size="small"
              type="primary"
              loading={savingField === field}
              onClick={() => saveField(field)}
            >
              {t('common:save')}
            </Button>
            <Button
              icon={<CloseOutlined />}
              size="small"
              onClick={() => cancelEditing(field)}
              disabled={savingField === field}
            >
              {t('common:cancel')}
            </Button>
          </Space>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%' }}>
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>{value}</div>
        <Tooltip title={t('orders:orderDetail.edit')}>
          <Button
            aria-label={t('orders:orderDetail.edit')}
            icon={<EditOutlined />}
            size="small"
            type="text"
            shape="circle"
            onClick={() => startEditing(field)}
            disabled={Boolean(editingField && editingField !== field)}
            style={{ flex: '0 0 auto', marginTop: -3, color: 'var(--app-text-secondary)' }}
          />
        </Tooltip>
      </div>
    );
  };

  if (loading) {
    return <Spin size="large" tip={t('orders:orderDetail.loading')} style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (orderError) {
    return (
      <Alert
        message={t('orders:orderDetail.messages.fetchError')}
        type="error"
        showIcon
        action={<Button size="small" onClick={() => { void refreshOrder().catch(() => undefined); }}>{t('common:retry')}</Button>}
      />
    );
  }

  if (!order) {
    return <div>{t('orders:orderDetail.orderNotFound')}</div>;
  }

  const linkedSupplierOrderIds = new Set(linkedSupplierOrders.map((supplierOrder) => supplierOrder.id));
  const availableSupplierOrders = supplierOrderOptions.filter(
    (supplierOrder) => !linkedSupplierOrderIds.has(supplierOrder.id)
  );
  const selectedSupplierOrderForLink = availableSupplierOrders.find(
    (supplierOrder) => supplierOrder.id === selectedSupplierOrderId
  );
  const selectedSupplierOrderItemOptions = (selectedSupplierOrderForLink?.items || []).map((item) => (
    buildSupplierOrderAllocationItemOption(
      item,
      item.id,
      `${item.name} (${formatSupplierOrderQuantity(item, t, i18n.language)})`,
    )
  ));

  const costColumns = [
    {
      title: t('orders:orderDetail.category'),
      dataIndex: 'category',
      key: 'category',
      render: (cat: string) => t(`orders:costCategories.${cat}`),
    },
    {
      title: t('orders:orderDetail.amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: any) => (
        <CurrencyAmount
          amount={record.originalCurrency && record.originalCurrency !== 'PLN' && record.originalAmount != null ? record.originalAmount : amount}
          currency={record.originalCurrency || 'PLN'}
          plnAmount={amount}
          rateDate={record.incurredAt}
        />
      ),
    },
    {
      title: t('orders:orderDetail.purchaseInvoice'),
      key: 'purchaseInvoice',
      render: (_: unknown, record: Cost) => {
        if (record.category !== 'PRODUCT_COST') {
          return '-';
        }

        if (!record.purchaseInvoiceNumber && !record.purchaseInvoiceDate) {
          return '-';
        }

        return (
          <div>
            {record.purchaseInvoiceNumber && (
              <div>
                {record.purchaseInvoiceNumber}
              </div>
            )}
            {record.purchaseInvoiceDate && (
              <div style={{ fontSize: '0.9em', color: 'var(--app-text-secondary)' }}>
                {formatDate(record.purchaseInvoiceDate)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('orders:orderDetail.costDescription'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('orders:orderDetail.addedBy'),
      dataIndex: ['addedBy', 'name'],
      key: 'addedBy',
    },
    {
      title: t('orders:orderDetail.date'),
      dataIndex: 'incurredAt',
      key: 'incurredAt',
      render: (date: string) => formatDate(date),
    },
    ...((canEditCosts || canEditTransportCosts) ? [{
      title: t('orders:orderDetail.actions'),
      key: 'actions',
      width: 140,
      render: (_: any, record: Cost) => (
        <Space size="small">
          <Button
            size="small"
            icon={isManagedTransportCost(record) ? <TruckOutlined /> : <EditOutlined />}
            disabled={
              (isManagedTransportCost(record) && !canEditTransportCosts)
              || (!isManagedTransportCost(record) && !canEditCustomerOrderFallbackCosts)
              || (!isManagedTransportCost(record) && !isManualCustomerOrderCost(record))
            }
            onClick={() => (
              isManagedTransportCost(record)
                ? openTransportManagement(record)
                : openEditCostModal(record)
            )}
          >
            {isManagedTransportCost(record) ? t('transport:editTransport') : t('common:edit')}
          </Button>
          {isManualCustomerOrderCost(record) && canEditCustomerOrderFallbackCosts && (
            <Tooltip title={t('orders:orderDetail.delete')}>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                aria-label={t('orders:orderDetail.delete')}
                onClick={() => handleDeleteCost(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    }] : []),
  ];
  const operationalCostColumns = costColumns.filter((column: any) => column.key !== 'purchaseInvoice');
  const purchaseInvoiceCostColumns = costColumns;

  const customerName = order.customer?.name || t('orders:orderDetail.archivedCustomer');
  const createdByName = order.createdBy?.name || t('orders:orderDetail.deletedUser');
  const orderCosts = Array.isArray(order.costs) ? order.costs : [];
  const operationalCosts = orderCosts.filter(isOperationalCustomerOrderCost);
  const customerFallbackCosts = orderCosts.filter(isManualCustomerOrderCost);
  const customerOrderCostRows = canViewCustomerOrderFinancialCosts
    ? operationalCosts
    : customerFallbackCosts;
  const purchaseInvoiceCosts = orderCosts.filter(isPurchaseInvoiceCost);
  const orderStatusHistory = Array.isArray(order.statusHistory) ? order.statusHistory : [];
  // Money is stored in PLN (migration 054). Display stored values directly.
  const toDisplayAmount = (amount: number | null | undefined) => Number(amount ?? 0);
  const getCostAmountInPln = (cost: any) => toDisplayAmount(cost?.amount);
  const revenueDisplayAmount = toDisplayAmount(order.revenue);
  const totalCostDisplayAmount = toDisplayAmount(order.totalCost);
  const marginDisplayAmount = toDisplayAmount(order.margin);
  const statusBadgeStyle = getStatusBadgeStyle(order.status);
  const marginPercent = order.marginPercent || 0;
  const marginTone = getOrderMarginTone(marginDisplayAmount, marginPercent);
  const marginStatusKey = marginTone === 'loss'
    ? 'marginResultNegative'
    : marginTone === 'watch'
      ? 'marginResultWatch'
      : 'marginResultPositive';
  const marginPercentLabel = hasRevenueForMarginPercent(revenueDisplayAmount)
    ? `${marginPercent.toLocaleString(
        i18n.language.startsWith('pl') ? 'pl-PL' : 'en-US',
        { minimumFractionDigits: 1, maximumFractionDigits: 1 },
      )}%`
    : t('orders:orderDetail.marginPercentUnavailable');
  const isFinancialCompletenessPending = canShowOrderFinancialCompletenessStatus(
    canViewCustomerSalePrices,
    order.financialCompleteness,
  );
  const salePricesIncomplete = canShowOrderSalePricesIncompleteHint(
    canViewCustomerSalePrices,
    order.financialCompleteness,
  );
  const customerPaymentStatus = order.customerPaymentStatus || 'PENDING';
  const customerPaymentDueDate = order.customerPaymentDueDate ? dayjs(order.customerPaymentDueDate) : null;
  const isCustomerPaymentOverdue = Boolean(
    customerPaymentStatus !== 'PAID' &&
    customerPaymentDueDate &&
    customerPaymentDueDate.isBefore(dayjs(), 'day')
  );
  const customerAccentColor = '#0f766e';
  const customerAccentBackground = '#ecfdf5';
  const customerWorkflowStatuses = getCustomerOrderProgressStatuses(order.status, orderStatusHistory);
  const customerWorkflowCurrentKey = order.status;
  const isCustomerWorkflowCancelled = order.status === 'CANCELLED';
  const isCustomerWorkflowHeld = order.status === 'ON_HOLD';
  const customerWorkflowSteps = customerWorkflowStatuses.map((status) => ({
    key: status,
    label: t(`orders:statusOptions.${status}`),
  }));
  const customerWorkflowCurrentLabel = t(
    isCustomerWorkflowCancelled
      ? 'orders:orderDetail.cancelledWorkflow'
      : isCustomerWorkflowHeld
        ? 'orders:orderDetail.heldWorkflow'
        : 'orders:orderDetail.currentStatus'
  );
  const overviewMeta = [
    {
      icon: <UserOutlined />,
      label: t('orders:orderDetail.customer'),
      value: customerName,
    },
    {
      icon: <UserOutlined />,
      label: t('orders:orderDetail.accountManager'),
      value: order.accountManagerName || createdByName || '-',
    },
    {
      icon: <CalendarOutlined />,
      label: t('orders:orderDetail.orderDate'),
      value: formatDate(order.orderDate),
    },
    {
      icon: <TruckOutlined />,
      label: t('orders:orderDetail.expectedDelivery'),
      value: formatDate(order.expectedDelivery),
    },
    ...(!currentUser?.invoiceWorkflowEnabled && canManageOrderOperations && fulfillmentSource
      ? [{
          icon: <CheckSquareOutlined />,
          label: t('orders:orderDetail.fulfillmentSource'),
          value: (
            <Tag color={fulfillmentSource === 'MIXED' ? 'purple' : fulfillmentSource === 'WAREHOUSE' ? 'blue' : fulfillmentSource === 'SUPPLIER' ? 'cyan' : 'default'}>
              {t(`orders:orderDetail.fulfillmentSources.${fulfillmentSource}`)}
            </Tag>
          ),
        }]
      : []),
  ];

  return (
    <div style={{ paddingBottom: 24 }}>
      <div style={PAGE_SHELL_STYLE} className="order-detail-page">
      <PresenceBanner scopeType="ORDER" others={presenceOthers} style={{ marginBottom: 16 }} />
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        style={getOrderTypeHeaderStyle(customerAccentColor)}
      >
        <div style={{ flex: '1 1 100%', minWidth: 0 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/app/orders')}
            style={{ marginBottom: 12, borderRadius: 999, fontWeight: 600 }}
          >
            {t('orders:orderDetail.backToOrders')}
          </Button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            <span style={getOrderTypeBadgeStyle(customerAccentColor, customerAccentBackground)}>
              <UserOutlined />
              {t('orders:orderDetail.customerOrderType')}
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--app-text-secondary)' }}>
              {t('orders:orderDetail.title')}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            <div style={{ fontSize: 'clamp(1.45rem, 2.8vw, 2.3rem)', lineHeight: 1.05, fontWeight: 800, letterSpacing: 0, color: 'var(--app-text)', overflowWrap: 'anywhere' }}>
              {getDisplayOrderNumber(order)}
            </div>
            {canManageOrderOperations && (
              <Tooltip title={t('orders:orderDetail.editNumber')}>
                <Button
                  aria-label={t('orders:orderDetail.editNumber')}
                  icon={<EditOutlined />}
                  type="text"
                  size="small"
                  shape="circle"
                  onClick={() => setNumberModalOpen(true)}
                  style={{ color: 'var(--app-text-muted)' }}
                />
              </Tooltip>
            )}
            <Modal
              open={numberModalOpen}
              title={t('orders:orderDetail.editNumber')}
              onCancel={() => setNumberModalOpen(false)}
              onOk={saveOrderNumber}
              okText={t('common:save')}
              cancelText={t('common:cancel')}
              confirmLoading={savingField === 'manualNumbering'}
              destroyOnClose
            >
              <Form form={numberForm} layout="vertical" initialValues={getNumberModalInitialValues()}>
                <Form.Item
                  name="useManualNumbering"
                  valuePropName="checked"
                  label={t('orders:orderDetail.useManualNumbering')}
                >
                  <Switch />
                </Form.Item>
                {numberFormUseManual && (
                  <Form.Item
                    name="manualOrderNumber"
                    label={t('orders:orderNumber')}
                    rules={[
                      { required: true, whitespace: true, message: t('orders:modal.validation.manualNumberRequired') },
                      customerOrderNumberRule(t),
                    ]}
                  >
                    <Input maxLength={64} placeholder={t('orders:orderDetail.manualOrderNumberPlaceholder')} />
                  </Form.Item>
                )}
              </Form>
            </Modal>
            <OrderSettlementReadyModal
              open={partialSettlementModalOpen}
              orderId={id!}
              readiness={settlementReadiness}
              canViewFinance={canEnterSettlementFinancialValues}
              onFinalize={canFinalizeFromResult ? handleSettleOrder : undefined}
              onClose={() => setPartialSettlementModalOpen(false)}
              onRefetch={refreshSettlementCommandReadModels}
              onResult={(result) => handleSettlementCommandResult(result)}
            />
            <Modal
              open={correctionModalOpen}
              title={t('orders:orderDetail.correctionTitle')}
              onCancel={() => {
                setCorrectionModalOpen(false);
                setCorrectionTarget(null);
                correctionRetryKey.current = null;
                correctionInFlight.current = false;
                correctionForm.resetFields();
              }}
              onOk={() => correctionForm.submit()}
              okText={t('orders:orderDetail.saveCorrection')}
              cancelText={t('common:cancel')}
              confirmLoading={settlementSubmitting}
              destroyOnClose
            >
              <Form
                form={correctionForm}
                layout="vertical"
                onFinish={submitCorrection}
              >
                <Alert
                  type="warning"
                  showIcon
                  message={t('orders:orderDetail.correctionHint')}
                  description={correctionTarget ? `${t('orders:orderDetail.correctionTarget')}: ${formatDate(correctionTarget.recognizedAt)} — ${t(`orders:orderDetail.settlementKinds.${correctionTarget.kind}`)}` : undefined}
                  style={{ marginBottom: 16 }}
                />
                <Form.Item
                  name="recognizedAt"
                  label={t('orders:orderDetail.settlementDate')}
                  rules={[{ required: true, message: t('orders:orderDetail.settlementDateRequired') }]}
                >
                  <Input type="date" />
                </Form.Item>
                <Form.Item
                  name="revenue"
                  label={t('orders:orderDetail.correctionRevenue')}
                  extra={t('orders:orderDetail.correctionSignedHint')}
                  rules={[{ required: true, message: t('orders:orderDetail.correctionAmountRequired') }]}
                >
                  <InputNumber precision={2} style={{ width: '100%' }} {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} />
                </Form.Item>
                <Form.Item
                  name="totalCost"
                  label={t('orders:orderDetail.correctionCost')}
                  extra={t('orders:orderDetail.correctionSignedHint')}
                  rules={[{ required: true, message: t('orders:orderDetail.correctionAmountRequired') }]}
                >
                  <InputNumber precision={2} style={{ width: '100%' }} {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} />
                </Form.Item>
                <Form.Item
                  name="note"
                  label={t('orders:orderDetail.correctionNote')}
                  rules={[{ required: true, whitespace: true, message: t('orders:orderDetail.correctionNoteRequired') }]}
                >
                  <Input.TextArea maxLength={1000} rows={3} />
                </Form.Item>
              </Form>
            </Modal>
            {order.useManualNumbering && (
              <Tag color="blue" style={{ borderRadius: 999, paddingInline: 10, fontWeight: 600 }}>
                {t('orders:orderDetail.manual')}
              </Tag>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            <span style={statusBadgeStyle}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: 'currentColor' }} />
              {t(`orders:statusOptions.${order.status}`)}
            </span>
            {canCreatePartialSettlement && !settlementDetails?.invoiceBased && (
              <Tooltip
                title={settlementReadinessLoading
                  ? t('orders:orderDetail.settlementCheckingReadiness')
                  : settlementReadinessError
                    ? t('orders:orderDetail.settlementReadinessLoadError')
                    : canFinalizeFromResult
                      ? t('orders:orderDetail.settlementCommandReadyHint')
                    : canEnterSettlementFinancialValues && settlementReadiness?.financialBlockedReasons?.length
                      ? settlementReadiness.financialBlockedReasons[0].message
                    : settlementReadiness?.commandState === 'ADMIN_REQUIRED'
                      ? t('orders:orderDetail.settlementCommandAdminRequiredDescription')
                      : settlementReadiness?.commandState === 'SUPERSEDED'
                        ? t('orders:orderDetail.settlementCommandSuperseded')
                        : settlementReadiness?.canSubmit
                          ? t('orders:orderDetail.settlementCommandReadyHint')
                          : t('orders:orderDetail.settlementCommandPhysicalCheckHint')}
              >
                <span>
                  <Button
                    type="primary"
                    icon={<DollarOutlined />}
                    loading={settlementReadinessLoading}
                    disabled={settlementReadinessLoading || settlementReadinessError || !(canSubmitSettlementCommand || canFinalizeFromResult)}
                    onClick={() => setPartialSettlementModalOpen(true)}
                    style={{ borderRadius: 999, fontWeight: 600 }}
                  >
                    {t('orders:orderDetail.settlementCommandSubmit')}
                  </Button>
                </span>
              </Tooltip>
            )}
            <OrderSettlementCommandStatus state={settlementReadiness?.commandState} />
            {settlementReadiness?.actions.finalizeWithRemainder
              && !order.isSettled
              && canCloseSettlement && !settlementDetails?.invoiceBased && (
              <Button
                danger
                loading={settlementSubmitting}
                onClick={() => setIncompleteClosureOpen(true)}
                style={{ borderRadius: 999, fontWeight: 600 }}
              >
                {t('orders:orderDetail.closeWithRemainder')}
              </Button>
            )}
            {isFinancialCompletenessPending && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  borderRadius: 999,
                  fontWeight: 700,
                  fontSize: 13,
                  background: '#fffbeb',
                  border: '1px solid #fde68a',
                  color: '#92400e',
                }}
              >
                {t('orders:orderDetail.financialCompletenessPending')}
              </span>
            )}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '8px 12px',
                borderRadius: 999,
                fontWeight: 700,
                fontSize: 13,
                background: 'var(--app-surface)',
                border: '1px solid var(--app-border)',
                color: 'var(--app-text)',
              }}
            >
              {order.currency || 'PLN'}
            </span>
            {order.commerciallyFrozenAt && <Tag>{t('invoices:workflow.frozenOrder')}</Tag>}
            {authService.canManageInvoiceImports() && <Button onClick={() => navigate(`/app/sales-invoices/new?orderId=${order.id}`)}>{t('invoices:workflow.page.salesTitle')}</Button>}
            {canChangeOrderStatus && (
              <Button type="primary" onClick={() => setStatusModalVisible(true)} style={{ borderRadius: 999, fontWeight: 600 }}>
                {t('orders:orderDetail.updateStatus')}
              </Button>
            )}
            {canManageOrderOperations && canViewSalesDocuments && (
              <Tooltip title={customerConfirmationDisabledReason}>
                <Button
                  icon={<MailOutlined />}
                  disabled={!canSendCustomerConfirmation}
                  onClick={() => setCustomerEmailModalVisible(true)}
                  style={{ borderRadius: 999, fontWeight: 600 }}
                >
                  {t('orders:orderDetail.sendCustomerConfirmation')}
                </Button>
              </Tooltip>
            )}
            {canManageOrderOperations && (
              <Tooltip title={canSendCustomerStatusEmail ? '' : customerStatusEmailDisabledReason}>
                <Button
                  icon={<MailOutlined />}
                  disabled={!canSendCustomerStatusEmail}
                  onClick={() => setStatusEmailModalVisible(true)}
                  style={{ borderRadius: 999, fontWeight: 600 }}
                >
                  {t('orders:orderDetail.sendCustomerStatusEmail')}
                </Button>
              </Tooltip>
            )}
            {canViewSalesDocuments && (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 8,
                  padding: '6px 8px',
                  borderRadius: 999,
                  background: 'var(--app-surface)',
                  border: '1px solid var(--app-border)',
                  maxWidth: '100%',
                }}
              >
                <Segmented<OrderDocumentLanguage>
                  size="small"
                  value={orderDocumentLanguage}
                  onChange={(value) => setOrderDocumentLanguage(value as OrderDocumentLanguage)}
                  options={[
                    { label: 'EN', value: 'en' },
                    { label: 'PL', value: 'pl' },
                  ]}
                />
                <Button
                  icon={<FilePdfOutlined />}
                  loading={orderDocumentDownloading}
                  onClick={handleDownloadOrderDocument}
                  style={{ borderRadius: 999, fontWeight: 600, maxWidth: '100%', whiteSpace: 'normal', height: 'auto' }}
                >
                  {t('orders:orderDetail.downloadOrderDocument')}
                </Button>
              </div>
            )}
          </div>
          {canCreatePartialSettlement
            && settlementReadiness
            && settlementTrancheLabel
            && settlementReadiness.commandState !== 'SUPERSEDED' && (
            <Alert
              type={settlementReadiness.commandState === 'ADMIN_REQUIRED' ? 'warning' : 'info'}
              showIcon
              message={settlementReadiness.commandState === 'ADMIN_REQUIRED'
                ? t('orders:orderDetail.settlementTrancheWaitingTitle', { quantity: settlementTrancheLabel })
                : t('orders:orderDetail.settlementTrancheReadyTitle', { quantity: settlementTrancheLabel })}
              description={settlementReadiness.commandState === 'ADMIN_REQUIRED'
                ? t('orders:orderDetail.settlementTrancheWaitingDescription')
                : t('orders:orderDetail.settlementTrancheReadyDescription', {
                  transports: settlementBasisTransportLabel || t('orders:orderDetail.settlementTransportWithoutNumber'),
                })}
              style={{ marginBottom: 12, borderRadius: 12 }}
            />
          )}
          {showCustomerConfirmationBlockedReason && (
            <Alert
              type="warning"
              showIcon
              message={customerConfirmationDisabledReason}
              style={{ marginBottom: 12, borderRadius: 12 }}
            />
          )}

          <div style={DETAIL_META_GRID_STYLE}>
            {overviewMeta.map((item) => (
              <div
                key={item.label}
                style={{
                  display: 'flex',
                  gap: 10,
                  padding: '12px 14px',
                  borderRadius: 14,
                  background: 'var(--app-surface-hover)',
                  border: '1px solid var(--app-border)',
                }}
              >
                <div style={{ fontSize: 15, color: '#2563eb', marginTop: 1 }}>{item.icon}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--app-text-secondary)', marginBottom: 4 }}>{item.label}</div>
                  <div style={{ color: 'var(--app-text)', fontSize: 15, fontWeight: 600, lineHeight: 1.4, wordBreak: 'break-word' }}>{item.value}</div>
                </div>
              </div>
            ))}
          </div>
          {canViewOrderTransports && !transportLoading && (
            <OrderTransportStatusStrip transports={orderTransports.transports} />
          )}
        </div>
      </motion.div>

      {order.isQuote && (
        <Alert
          type="info"
          showIcon
          message={t('orders:orderDetail.quoteBanner')}
          action={
            canManageOrderOperations && (
              <Button size="small" type="primary" onClick={handleConvertQuote}>
                {t('orders:orderDetail.convertToOrder')}
              </Button>
            )
          }
          style={{ marginBottom: 16 }}
        />
      )}
      {order.quoteConvertedAt && !order.isQuote && (
        <Alert
          type="success"
          showIcon
          message={t('orders:orderDetail.convertedFromQuote', { date: formatDate(order.quoteConvertedAt) })}
          style={{ marginBottom: 16 }}
        />
      )}

      <OrderWorkflowRail
        title={t('orders:orderDetail.workflow')}
        steps={customerWorkflowSteps}
        currentKey={customerWorkflowCurrentKey}
        currentLabel={customerWorkflowCurrentLabel}
        cancelled={isCustomerWorkflowCancelled}
        cancelledText={t('orders:orderDetail.cancelledWorkflow')}
        held={isCustomerWorkflowHeld}
        heldText={t('orders:orderDetail.heldWorkflow')}
      />

      <Tabs
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
        size="large"
        style={{ maxWidth: '100%', minWidth: 0 }}
        tabBarStyle={{ marginBottom: 24, fontWeight: 600, maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}
      >
        <Tabs.TabPane tab={t('orders:orderDetail.orderInfo')} key="1">
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.03 }}>
              {currentUser?.invoiceWorkflowEnabled && authService.canViewWarehouseDocuments() && (
                <OrderWarehouseSection orderId={order.id} canViewMargin={canViewMargin} refreshKey={order} />
              )}
              <OrderProductsSection
                order={order}
                canEditProducts={canEditProducts}
                canViewSalePrices={canViewCustomerSalePrices}
                canViewMargin={canViewMargin}
                canEditSalePrices={canEditSalesDocuments}
                salePricesIncomplete={salePricesIncomplete}
                onSaved={refreshOrderFinancials}
              />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 }}>
              <OrderInfoAccordion
                order={order}
                customerName={customerName}
                createdByName={createdByName}
                accountManagers={accountManagers}
                suppliers={suppliers}
                customerPaymentStatus={customerPaymentStatus}
                canViewCustomerPayments={canViewCustomerPayments}
                isCustomerPaymentOverdue={isCustomerPaymentOverdue}
                useManualNumberingValue={Boolean(useManualNumberingValue)}
                editingField={editingField}
                editForm={editForm}
                renderEditableField={renderEditableField}
                canManageOrderOperations={canManageOrderOperations}
                canEditCustomerOrderDocuments={canEditCustomerOrderDocuments}
              />
            </motion.div>
          </Space>
        </Tabs.TabPane>

        <Tabs.TabPane
          tab={
            <span>
              <FileTextOutlined />
              {t('orders:orderDetail.activity')}
            </span>
          }
          key="activity"
        >
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.03 }}>
              <Card
                title={renderSectionTitle(t('orders:orderDetail.statusHistory'))}
                style={SECTION_CARD_STYLE}
                headStyle={SECTION_CARD_HEAD_STYLE}
                bodyStyle={{ ...SECTION_CARD_BODY_STYLE, paddingTop: 16 }}
              >
                <Timeline style={{ marginBottom: 0 }}>
                  {orderStatusHistory.map((history: any) => (
                    <Timeline.Item key={history.id}>
                      <div>
                        <strong>{t(`orders:statusOptions.${history.status}`)}</strong>
                        <div style={{ fontSize: 12, color: 'var(--app-text-muted)' }}>
                          {formatDateTime(history.changedAt)}
                        </div>
                        {history.notes && <div style={{ fontSize: 12 }}>{history.notes}</div>}
                      </div>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Card>
            </motion.div>

            {/* Notification Assignments - Admin only */}
            {canManageNotificationAssignments && (
              <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.06 }}>
                <OrderNotificationAssignments orderId={id!} />
              </motion.div>
            )}

            {/* Communications sent for this order - all roles */}
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.09 }}>
              <OrderCommunications orderId={id!} />
            </motion.div>
          </Space>
        </Tabs.TabPane>

    {canViewOrderTransports && (
    <Tabs.TabPane
      tab={
        <span>
          <TruckOutlined />
          {t('transport:transport')}
          {orderTransports.transports.length > 0 && (
            <Tag style={{ marginLeft: 8 }}>{orderTransports.transports.length}</Tag>
          )}
        </span>
      }
      key="2"
    >
      <OrderTransportsSection
        orderId={id!}
        orderTransports={orderTransports}
        loading={transportLoading}
        canEdit={canManageOrderOperations}
        canDelete={canDeleteTransportLink}
        canManageStatus={canManageTransportStatus}
        canViewCosts={authService.canViewTransportCosts()}
        onDelete={handleUnlinkTransport}
        onStatusChange={handleTransportStatusChange}
        readiness={settlementReadiness}
      />
    </Tabs.TabPane>
    )}

    {canViewCustomerOrderSupplierLinks && (
    <Tabs.TabPane
      tab={
        <span>
          <LinkOutlined />
          {t('orders:orderDetail.relations')}
          {linkedSupplierOrders.length > 0 && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {linkedSupplierOrders.length}
            </Tag>
          )}
        </span>
      }
      key="3"
    >
      <Card
          title={renderSectionTitle(t('orders:orderDetail.linkedSupplierOrders'))}
          style={SECTION_CARD_STYLE}
          headStyle={SECTION_CARD_HEAD_STYLE}
          bodyStyle={SECTION_CARD_BODY_STYLE}
          extra={
            canManageSupplierOrders && (
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={openSupplierOrderLinkModal}
              >
                {t('orders:supplierOrders.linkSupplierOrder')}
              </Button>
            )
          }
        >
          <Table
            dataSource={linkedSupplierOrders}
            rowKey={(record) => record.linkId || record.id}
            size="middle"
            pagination={false}
            scroll={{ x: 1240 }}
            locale={{ emptyText: t('orders:orderDetail.noLinkedSupplierOrders') }}
            columns={[
              {
                title: t('orders:supplierOrders.table.supplierOrderNumber'),
                key: 'orderNumber',
                render: (_value, record: SupplierOrderDto) => (
                  <Link to={`/app/supplier-orders/${record.id}`}>
                    {getSupplierOrderDisplayNumber(record)}
                  </Link>
                ),
              },
              {
                title: t('orders:supplierOrders.table.supplier'),
                key: 'supplier',
                render: (_value, record: SupplierOrderDto) => (
                  record.supplier?.name
                    ? <Link to={buildSupplierSearchPath(record.supplier.name)}>{record.supplier.name}</Link>
                    : '-'
                ),
              },
              {
                title: t('orders:supplierOrders.table.factory'),
                key: 'factory',
                render: (_value, record: SupplierOrderDto) => record.factory?.name || '-',
              },
              {
                title: t('orders:supplierOrders.form.supplierOrderItem'),
                key: 'supplierOrderItem',
                render: (_value, record: SupplierOrderDto) => {
                  if (record.itemAllocations?.length) {
                    return (
                      <Space direction="vertical" size={2}>
                        {getSupplierOrderLinkItemNames(record).map((name, index) => (
                          <span key={record.itemAllocations?.[index]?.id || `${record.linkId}-item-${index}`}>
                            {name}
                          </span>
                        ))}
                      </Space>
                    );
                  }
                  if (!record.supplierOrderItemName) {
                    return t('orders:supplierOrders.form.linkWholeOrder');
                  }
                  if (!record.supplierOrderItemProductId) return record.supplierOrderItemName;

                  return (
                    <Link to={`/app/products/${record.supplierOrderItemProductId}`}>
                      {record.supplierOrderItemName}
                    </Link>
                  );
                },
              },
              {
                title: t('orders:supplierOrders.form.allocatedQuantity'),
                key: 'allocatedQuantity',
                align: 'right' as const,
                render: (_value, record: SupplierOrderDto) => {
                  const labels = getSupplierOrderLinkQuantityLabels(
                    record,
                    i18n.language.startsWith('pl') ? 'pl-PL' : 'en-US',
                  );
                  if (labels.length === 0) return '—';

                  return (
                    <Space direction="vertical" size={2}>
                      {labels.map((label, index) => (
                        <span key={record.itemAllocations?.[index]?.id || `${record.linkId}-quantity-${index}`}>
                          {label}
                        </span>
                      ))}
                    </Space>
                  );
                },
              },
              ...(canViewSupplierOrderFinance ? [{
                title: t('orders:orderDetail.purchaseCost'),
                dataIndex: 'effectiveAllocatedPurchaseCostPLN',
                key: 'effectiveAllocatedPurchaseCostPLN',
                align: 'right' as const,
                render: (value: number | undefined, record: SupplierOrderDto) => (
                  isManualSupplierAllocation(record) ? t('orders:supplierOrders.detail.manualAllocation') : formatOrderMoney(value || 0, 'PLN')
                ),
              }, {
                title: t('orders:orderDetail.landedCost'),
                dataIndex: 'effectiveAllocatedLandedCostPLN',
                key: 'effectiveAllocatedLandedCostPLN',
                align: 'right' as const,
                render: (value: number | undefined, record: SupplierOrderDto) => (
                  isManualSupplierAllocation(record) ? '-' : formatOrderMoney(value || 0, 'PLN')
                ),
              }] : []),
            ]}
            summary={(data) => canViewSupplierOrderFinance ? (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5}>
                  <strong>{t('orders:orderDetail.total')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <strong>{formatOrderMoney(data.reduce((sum: number, item: SupplierOrderDto) => sum + Number(item.effectiveAllocatedPurchaseCostPLN || 0), 0), 'PLN')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right">
                  <strong>{formatOrderMoney(data.reduce((sum: number, item: SupplierOrderDto) => sum + Number(item.effectiveAllocatedLandedCostPLN || 0), 0), 'PLN')}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            ) : null}
          />
          {canViewSupplierOrderFinance && linkedSupplierOrders.length > 0 && (
            <div className="order-supplier-links-total">
              <div>
                <div className="order-supplier-links-total__label">{t('orders:orderDetail.total')}</div>
                <div className="order-supplier-links-total__hint">
                  {t('orders:orderDetail.purchaseAndLandedCost')}
                </div>
              </div>
              <strong className="order-supplier-links-total__value">
                {formatOrderMoney(
                  linkedSupplierOrders.reduce(
                    (sum, supplierOrder) => sum + getSupplierAllocationTotalCost(supplierOrder),
                    0,
                  ),
                  'PLN',
                )}
              </strong>
            </div>
          )}
        </Card>
    </Tabs.TabPane>
    )}

    {(canViewCustomerOrderCostSection || canViewCustomerSalePrices || canViewPurchaseDocuments || canViewMargin) && (
    <Tabs.TabPane
      tab={
        <span>
          <DollarOutlined />
          {t('orders:orderDetail.finances')}
        </span>
      }
      key="4"
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {canEnterSettlementFinancialValues && !settlementDetails?.invoiceBased && (
          <OrderSettlementCommandResolutionPanel
            orderId={id!}
            readiness={settlementReadiness}
            canViewFinance={canEnterSettlementFinancialValues}
            onRefetch={refreshSettlementCommandReadModels}
            onResult={(result) => handleSettlementCommandResult(result, 'ADMIN_RESOLUTION')}
          />
        )}
        {canViewMargin && !currentUser?.invoiceWorkflowEnabled && order.financialCompleteness?.missing?.includes('CUSTOMER_ORDER_COSTS') && (
          <Alert
            type="warning"
            showIcon
            message={t('orders:orderDetail.missingCustomerOrderCostsTitle')}
            description={t('orders:orderDetail.missingCustomerOrderCostsDescription')}
          />
        )}
        {canViewMargin && !settlementDetails?.invoiceBased && revenueDisplayAmount <= 0 && totalCostDisplayAmount > 0 && (
          <Alert
            type="info"
            showIcon
            message={t('orders:orderDetail.costWithoutRevenueTitle')}
            description={t('orders:orderDetail.costWithoutRevenueDescription')}
          />
        )}
        {canViewMargin && (
        <Card
          title={renderSectionTitle(t('orders:orderDetail.financialSummary'))}
          style={SECTION_CARD_STYLE}
          headStyle={SECTION_CARD_HEAD_STYLE}
          bodyStyle={{ ...SECTION_CARD_BODY_STYLE, paddingTop: 16 }}
        >
          {!settlementDetails?.invoiceBased && order.settlementStatus && order.settlementStatus !== 'OPEN' && (
            <Alert
              type={order.settlementStatus === 'SETTLED' ? 'success' : 'info'}
              showIcon
              message={order.settlementStatus === 'SETTLED'
                ? t('orders:orderDetail.settlementClosed')
                : t('orders:orderDetail.settlementPartiallyRecognized')}
              description={(
                <Space wrap>
                  <span>
                    {t('orders:orderDetail.settlementRecognizedRevenue')}: {formatOrderMoney(order.settledRevenue || 0, 'PLN')}
                  </span>
                  <span>
                    {t('orders:orderDetail.settlementRecognizedCost')}: {settlementDetails?.invoiceBased && settlementDetails.settled?.totalCost == null ? pendingInvoiceCostLabel : formatOrderMoney(settlementDetails?.settled?.totalCost ?? order.settledTotalCost ?? 0, 'PLN')}
                  </span>
                  <span>
                    {t('orders:orderDetail.settlementRecognizedMargin')}: {settlementDetails?.invoiceBased && settlementDetails.settled?.margin == null ? pendingInvoiceCostLabel : formatOrderMoney(settlementDetails?.settled?.margin ?? order.settledMargin ?? 0, 'PLN')}
                  </span>
                </Space>
              )}
              style={{ marginBottom: 16 }}
            />
          )}
          {settlementDetails?.requiresUpdate && (
            <Alert type="warning" showIcon
              message={t('orders:orderDetail.resultNeedsUpdate')}
              description={t('orders:orderDetail.resultNeedsUpdateDescription')} />
          )}
          {settlementDetails && settlementDetails.entries.length > 0 && (
            <Card
              size="small"
              title={t('orders:orderDetail.settlementHistory')}
              style={{ marginBottom: 16 }}
            >
              <Table<OrderSettlementEntry>
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: 760 }}
                dataSource={settlementDetails.entries}
                columns={[
                  {
                    title: t('orders:orderDetail.settlementDate'),
                    dataIndex: 'recognizedAt',
                    key: 'recognizedAt',
                    render: (value: string) => formatDate(value),
                  },
                  {
                    title: t('orders:orderDetail.settlementKind'),
                    dataIndex: 'kind',
                    key: 'kind',
                    render: (value: OrderSettlementEntry['kind']) => settlementDetails.invoiceBased && value === 'PARTIAL'
                      ? t('orders:orderDetail.settlementInvoiceEntry')
                      : t(`orders:orderDetail.settlementKinds.${value}`),
                  },
                  {
                    title: t('orders:orderDetail.settlementRevenue'),
                    dataIndex: 'revenue',
                    key: 'revenue',
                    align: 'right',
                    render: (value: number | null) => value === null ? pendingInvoiceCostLabel : formatOrderMoney(value, 'PLN'),
                  },
                  {
                    title: t('orders:orderDetail.settlementCost'),
                    dataIndex: 'totalCost',
                    key: 'totalCost',
                    align: 'right',
                    render: (value: number | null) => value === null ? pendingInvoiceCostLabel : formatOrderMoney(value, 'PLN'),
                  },
                  {
                    title: t('orders:orderDetail.settlementNote'),
                    dataIndex: 'note',
                    key: 'note',
                    render: (value: string | null) => value || '—',
                  },
                  {
                    title: t('orders:orderDetail.settlementActions'),
                    key: 'actions',
                    render: (_: unknown, entry: OrderSettlementEntry) => (
                      canEnterSettlementFinancialValues && !settlementDetails?.invoiceBased && canCorrectSettlementEntry(entry)
                    ) ? (
                      <Button size="small" onClick={() => openCorrectionModal(entry)}>
                        {t('orders:orderDetail.correctSettlement')}
                      </Button>
                    ) : null,
                  },
                ]}
              />
            </Card>
          )}
          {settlementDetails?.invoiceBased ? <>
            <Alert type={settlementDetails.costStatus === 'PENDING' ? 'warning' : 'info'} message={settlementDetails.costStatus === 'PENDING' ? pendingInvoiceCostLabel : t('invoices:workflow.page.invoiceFinancialBasis')} style={{ marginBottom: 16 }} />
            <div className="order-financial-summary">
              {([
                ['revenue', 'revenue'], ['totalCost', 'totalCost'], ['margin', 'orderResult'], ['marginPercent', 'margin'],
              ] as const).map(([field, label]) => <div className="order-financial-summary__metric" key={field}>
                <span className="order-financial-summary__label">{t(`orders:orderDetail.${label}`)}</span>
                <strong className="order-financial-summary__value">{settlementDetails.actual[field] == null ? pendingInvoiceCostLabel : field === 'marginPercent' ? `${settlementDetails.actual[field]?.toLocaleString('pl-PL', { maximumFractionDigits: 2 })}%` : formatOrderMoney(settlementDetails.actual[field]!, 'PLN')}</strong>
              </div>)}
            </div>
          </> : <><div className="order-financial-summary">
            <div className="order-financial-summary__metric">
              <span className="order-financial-summary__label">{t('orders:orderDetail.revenue')}</span>
              <div className="order-financial-summary__value">
                <CurrencyAmount
                  amount={order.exchangeRate ? revenueDisplayAmount / order.exchangeRate : revenueDisplayAmount}
                  currency={order.currency}
                  plnAmount={revenueDisplayAmount}
                  rateDate={order.exchangeRateEffectiveDate || order.orderDate}
                  rateSource={order.exchangeRateSource}
                  rateTableNumber={order.exchangeRateTableNumber}
                  strong
                  primary="pln"
                />
              </div>
            </div>
            <div className="order-financial-summary__metric">
              <span className="order-financial-summary__label">{t('orders:orderDetail.totalCost')}</span>
              <strong className="order-financial-summary__value">{formatOrderMoney(totalCostDisplayAmount, 'PLN')}</strong>
            </div>
            <div className={`order-financial-summary__metric order-financial-summary__metric--result order-financial-summary__metric--${marginTone}`}>
              <span className="order-financial-summary__label">{t('orders:orderDetail.orderResult')}</span>
              <strong className="order-financial-summary__value">{formatOrderMoney(marginDisplayAmount, 'PLN')}</strong>
              <span className="order-financial-summary__meta">{t(`orders:orderDetail.${marginStatusKey}`)}</span>
            </div>
            <div className="order-financial-summary__metric">
              <span className="order-financial-summary__label">{t('orders:orderDetail.margin')}</span>
              <strong className="order-financial-summary__value">{marginPercentLabel}</strong>
            </div>
          </div>
          <OrderMarginBuildUp marginExplanation={marginExplanation} /></>}
        </Card>
      )}
      {canViewMargin && !settlementDetails?.invoiceBased && (
        <OrderMarginExplanationSection
          marginExplanation={marginExplanation}
          loading={marginExplanationLoading}
        />
      )}
      {canViewCustomerOrderCostSection && (
      <Card
        title={renderSectionTitle(t('orders:orderDetail.orderCosts'))}
        style={SECTION_CARD_STYLE}
        headStyle={SECTION_CARD_HEAD_STYLE}
        bodyStyle={SECTION_CARD_BODY_STYLE}
        extra={
          <Space>
            {canEditCustomerOrderFallbackCosts && (
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAddCostModal}>
                {t('orders:orderDetail.addCost')}
              </Button>
            )}
          </Space>
        }
      >
        <Table
          dataSource={customerOrderCostRows}
          columns={operationalCostColumns}
          rowKey="id"
          size="middle"
          pagination={false}
          locale={{ emptyText: t('orders:orderDetail.noOrderCosts') }}
          summary={(data) => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}><strong>{t('orders:orderDetail.total')}</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <strong>{formatOrderMoney(data.reduce((sum: number, cost: any) => sum + getCostAmountInPln(cost), 0), 'PLN')}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} colSpan={Math.max(operationalCostColumns.length - 2, 1)} />
            </Table.Summary.Row>
          )}
        />
      </Card>
      )}
      {canViewPurchaseDocuments && purchaseInvoiceCosts.length > 0 && (
      <Card
        title={renderSectionTitle(t('orders:orderDetail.purchaseInvoices'))}
        style={SECTION_CARD_STYLE}
        headStyle={SECTION_CARD_HEAD_STYLE}
        bodyStyle={SECTION_CARD_BODY_STYLE}
      >
        <Table
          dataSource={purchaseInvoiceCosts}
          columns={purchaseInvoiceCostColumns}
          rowKey="id"
          size="middle"
          pagination={false}
          locale={{ emptyText: t('orders:orderDetail.noPurchaseInvoices') }}
        />
      </Card>
      )}
      {canViewMargin && marginHistory.length > 0 && (
        <Card
          title={renderSectionTitle(t('orders:orderDetail.marginHistory'))}
          style={SECTION_CARD_STYLE}
          headStyle={SECTION_CARD_HEAD_STYLE}
          bodyStyle={{ ...SECTION_CARD_BODY_STYLE, paddingTop: 16 }}
          size="small"
        >
          <Timeline>
            {marginHistory.map((h) => {
              const historyMarginPercent = Number.isFinite(h.marginPercent) ? h.marginPercent : 0;
              const historyPercentLabel = hasRevenueForMarginPercent(h.revenue)
                ? formatLocalizedPercent(historyMarginPercent, i18n.language)
                : t('orders:orderDetail.marginPercentUnavailable');

              return (
                <Timeline.Item key={h.id} color={historyMarginPercent < 15 ? 'red' : 'green'}>
                  <div>
                    <strong>{historyPercentLabel}</strong> ({formatOrderMoney(h.margin, 'PLN')})
                    <div style={{ fontSize: 12, color: 'var(--app-text-secondary)' }}>
                      {t(`orders:orderDetail.marginHistoryReasons.${h.reason}`, { defaultValue: h.reason })} · {formatDateTime(h.changedAt)}
                    </div>
                    {h.changedBy && <div style={{ fontSize: 12 }}>{h.changedBy.name}</div>}
                  </div>
                </Timeline.Item>
              );
            })}
          </Timeline>
        </Card>
      )}
      </Space>
    </Tabs.TabPane>
    )}

    <Tabs.TabPane
      tab={
        <span>
          <CheckSquareOutlined />
          {t('tasks:orderDetailTab')}
          {orderTasks.length > 0 && <Tag color="blue" style={{ marginLeft: 8 }}>{orderTasks.length}</Tag>}
        </span>
      }
      key="5"
    >
      <Card
        title={t('tasks:orderDetailTab')}
        extra={
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => navigate(`/app/tasks`)}
          >
            {t('tasks:newTask')}
          </Button>
        }
      >
        {orderTasksLoading ? (
          <Spin />
        ) : orderTasks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--app-text-muted)' }}>
            {t('tasks:noTasks')}
          </div>
        ) : (
          <Table
            dataSource={orderTasks}
            rowKey="id"
            size="small"
            pagination={false}
            onRow={(record) => ({
              style: { cursor: 'pointer' },
              onClick: () => navigate('/app/tasks')
            })}
            columns={[
              {
                title: t('tasks:taskTitle'),
                dataIndex: 'title',
                key: 'title',
                render: (text: string) => <strong>{text}</strong>
              },
              {
                title: t('tasks:status'),
                dataIndex: 'status',
                key: 'status',
                width: 120,
                render: (status: string) => {
                  const colors: Record<string, string> = { NEW: 'default', IN_PROGRESS: 'processing', REVIEW: 'warning', DONE: 'success', CANCELLED: 'error' };
                  return <Tag color={colors[status]}>{t(`tasks:statuses.${status}`)}</Tag>;
                }
              },
              {
                title: t('tasks:priority'),
                dataIndex: 'priority',
                key: 'priority',
                width: 100,
                render: (priority: string) => {
                  const colors: Record<string, string> = { LOW: 'default', MEDIUM: 'blue', HIGH: 'orange', URGENT: 'red' };
                  return <Tag color={colors[priority]}>{t(`tasks:priorities.${priority}`)}</Tag>;
                }
              },
              {
                title: t('tasks:assignee'),
                dataIndex: 'assigneeName',
                key: 'assigneeName',
                width: 150,
                render: (name: string) => name ? <><UserOutlined /> {name}</> : <span style={{ color: 'var(--app-text-muted)', fontStyle: 'italic' }}>{t('tasks:unassigned')}</span>
              },
              {
                title: t('tasks:dueDate'),
                dataIndex: 'dueDate',
                key: 'dueDate',
                width: 120,
                render: (date: string, record: InternalTask) => {
                  if (!date) return '-';
                  const [y, m, d] = date.split('-').map(Number);
                  const dueLocal = new Date(y, m - 1, d);
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  const isOverdue = dueLocal < today && record.status !== 'DONE' && record.status !== 'CANCELLED';
                  return <Tag color={isOverdue ? 'red' : undefined}><CalendarOutlined /> {dueLocal.toLocaleDateString()}</Tag>;
                }
              }
            ]}
          />
        )}
      </Card>
    </Tabs.TabPane>
  </Tabs>

      <Modal
        title={t('orders:supplierOrders.linkSupplierOrder')}
        open={canManageSupplierOrders && supplierOrderLinkModalVisible}
        onCancel={closeSupplierOrderLinkModal}
        maskClosable={false}
        footer={null}
      >
        <Form
          form={supplierOrderLinkForm}
          layout="vertical"
          onFinish={handleLinkSupplierOrder}
          onFinishFailed={() => feedback.warning(t('orders:messages.fillRequired'))}
          scrollToFirstError
        >
          <Form.Item
            name="supplierOrderId"
            label={t('orders:supplierOrders.form.supplierOrder')}
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              loading={supplierOrderOptionsLoading}
              optionFilterProp="label"
              notFoundContent={
                supplierOrderOptionsLoading
                  ? <Spin size="small" />
                  : t('orders:supplierOrders.noAvailableSupplierOrders')
              }
              options={availableSupplierOrders.map((supplierOrder) => ({
                value: supplierOrder.id,
                label: [
                  getSupplierOrderDisplayNumber(supplierOrder),
                  supplierOrder.supplier?.name,
                  canViewSupplierOrderFinance ? formatOrderMoney(supplierOrder.totalAmountPLN || 0, 'PLN') : null,
                ].filter(Boolean).join(' - '),
              }))}
            />
          </Form.Item>
          <SupplierOrderLinkAllocationFields
            form={supplierOrderLinkForm}
            canEditSupplierOrderFinance={canEditSupplierOrderFinance}
            supplierOrderItemOptions={selectedSupplierOrderItemOptions}
            canonicalItemAllocations
            customerOrder={order}
            supplierOrderItems={selectedSupplierOrderForLink?.items || []}
          />
          <Form.Item name="notes" label={t('orders:notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={supplierOrderLinkSubmitting}>
                {t('common:save')}
              </Button>
              <Button onClick={closeSupplierOrderLinkModal} disabled={supplierOrderLinkSubmitting}>
                {t('common:cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingCost ? t('orders:orderDetail.editCost') : t('orders:orderDetail.addCost')}
        open={costModalVisible}
        onCancel={closeCostModal}
        maskClosable={false}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleAddCost}>
          <Form.Item
            name="currency"
            label={t('orders:currency')}
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="USD">{t('orders:currencies.USD')}</Select.Option>
              <Select.Option value="EUR">{t('orders:currencies.EUR')}</Select.Option>
              <Select.Option value="PLN">{t('orders:currencies.PLN')}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="amount"
            label={t('orders:orderDetail.amount')}
            rules={[{ required: true }, { type: 'number', max: MAX_COST_AMOUNT }]}
          >
            <InputNumber min={0} max={MAX_COST_AMOUNT} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="description"
            label={t('orders:orderDetail.costDescription')}
            rules={[{ required: true }]}
          >
            <Input.TextArea />
          </Form.Item>
          <Form.Item
            name="document"
            label={t('orders:orderDetail.document')}
            valuePropName="fileList"
            getValueFromEvent={normalizeUploadFileList}
          >
            <Upload beforeUpload={() => false} maxCount={1}>
              <Button icon={<UploadOutlined />}>{t('orders:orderDetail.uploadDocument')}</Button>
            </Upload>
          </Form.Item>
          {editingCost?.documentUrl && (
            <div style={{ marginBottom: 16 }}>
              {t('orders:orderDetail.document')}: <a href={getAuthFileUrl(editingCost.documentUrl)} target="_blank" rel="noreferrer">{editingCost.documentName || t('orders:orderDetail.document')}</a>
            </div>
          )}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submittingCost}>
                {editingCost ? t('common:save') : t('orders:orderDetail.addCost')}
              </Button>
              <Button onClick={closeCostModal} disabled={submittingCost}>
                {t('common:cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('orders:orderDetail.closeWithRemainder')}
        open={incompleteClosureOpen}
        okText={t('orders:orderDetail.confirmIncompleteClosure')}
        cancelText={t('common:cancel')}
        okButtonProps={{ danger: true, disabled: incompleteClosureReason.trim().length < 3 }}
        confirmLoading={settlementSubmitting}
        maskClosable={false}
        onOk={handleSettleWithRemainder}
        onCancel={() => {
          if (!settlementSubmitting) {
            setIncompleteClosureOpen(false);
            setIncompleteClosureReason('');
          }
        }}
      >
        <Alert
          type="warning"
          showIcon
          message={t('orders:orderDetail.incompleteClosureWarning')}
          description={t('orders:orderDetail.incompleteClosureDescription')}
          style={{ marginBottom: 16 }}
        />
        <label style={{ display: 'grid', gap: 8, fontWeight: 600 }}>
          {t('orders:orderDetail.incompleteClosureReason')}
          <Input.TextArea
            rows={4}
            maxLength={1000}
            showCount
            value={incompleteClosureReason}
            onChange={(event) => setIncompleteClosureReason(event.target.value)}
            placeholder={t('orders:orderDetail.incompleteClosureReasonPlaceholder')}
          />
        </label>
      </Modal>

      <Modal
        title={t('orders:orderDetail.updateStatus')}
        open={statusModalVisible}
        onCancel={() => {
          setStatusModalVisible(false);
        }}
        maskClosable={false}
        footer={null}
      >
        <Form form={statusForm} layout="vertical" onFinish={handleUpdateStatus} onFinishFailed={() => feedback.warning(t('orders:messages.fillRequired'))} scrollToFirstError>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('orders:statusSelection.informationalTitle')}
            description={t('orders:statusSelection.informationalDescription')}
          />
          <Form.Item
            name="status"
            label={t('orders:orderDetail.newStatus')}
            rules={[{ required: true }]}
          >
            <OrderStatusTransitionSelect
              statuses={CUSTOMER_ORDER_ACTIVE_STATUS_VALUES}
              currentStatus={order.status}
              allowedTransitions={getOrderAllowedTransitions(order)}
              getStatusLabel={(status) => t(`orders:statusOptions.${status}`, {
                defaultValue: t('common:unknownStatus'),
              })}
              currentLabel={t('orders:statusSelection.current')}
              unavailableLabel={t('orders:statusSelection.unavailable')}
              guardedStatuses={['ON_HOLD']}
              guardedStatusReason={t('orders:statusSelection.requiresExplicitAction')}
              guardedStatusActionLabel={() => t('orders:statusSelection.holdOrderAction')}
              guardedStatusHint={t('orders:statusSelection.holdOrderHint')}
              onGuardedStatusSelect={(status) => statusForm.setFieldValue('status', status)}
            />
          </Form.Item>
          <Form.Item name="notes" label={t('orders:orderDetail.notes')}>
            <Input.TextArea />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={statusSubmitting}>{t('orders:orderDetail.updateStatus')}</Button>
          </Form.Item>
        </Form>
      </Modal>

      <CustomerEmailEditorModal
        title={t('orders:orderDetail.sendCustomerConfirmation')}
        open={customerEmailModalVisible}
        recipientName={order?.customer?.name}
        recipientEmail={order?.customer?.email}
        subject={customerEmailSubject}
        message={customerEmailMessage}
        previewHtml={customerEmailPreview?.html || ''}
        editing={customerEmailEditing}
        previewAvailable={Boolean(customerEmailPreview)}
        previewLoading={customerEmailPreviewLoading}
        sending={customerEmailSending}
        okText={t('orders:orderDetail.sendCustomerConfirmationOk')}
        previewErrorText={t('orders:orderDetail.customerConfirmationPreviewError')}
        onCancel={() => {
          setCustomerEmailModalVisible(false);
          setCustomerEmailEditing(false);
        }}
        onSubmit={handleSendCustomerConfirmation}
        onEdit={() => setCustomerEmailEditing(true)}
        onDiscardEdit={() => {
          setCustomerEmailEditing(false);
          setCustomerEmailPreview(customerEmailDefaultPreview);
          setCustomerEmailSubject(customerEmailDefaultPreview?.subject || '');
          setCustomerEmailMessage(customerEmailDefaultPreview?.message || '');
        }}
        onSubjectChange={setCustomerEmailSubject}
        onMessageChange={setCustomerEmailMessage}
      />

      <CustomerEmailEditorModal
        title={t('orders:orderDetail.sendCustomerStatusEmail')}
        open={statusEmailModalVisible}
        recipientName={order?.customer?.name}
        recipientEmail={order?.customer?.email}
        subject={statusEmailSubject}
        message={statusEmailMessage}
        previewHtml={statusEmailPreview?.html || ''}
        editing={statusEmailEditing}
        previewAvailable={Boolean(statusEmailPreview)}
        previewLoading={statusEmailPreviewLoading}
        sending={statusEmailSending}
        okText={t('orders:orderDetail.sendCustomerStatusEmailOk')}
        previewErrorText={t('orders:orderDetail.customerStatusEmailPreviewError')}
        onCancel={() => {
          setStatusEmailModalVisible(false);
          setStatusEmailEditing(false);
        }}
        onSubmit={handleSendCustomerStatusEmail}
        onEdit={() => setStatusEmailEditing(true)}
        onDiscardEdit={() => {
          setStatusEmailEditing(false);
          setStatusEmailPreview(statusEmailDefaultPreview);
          setStatusEmailSubject(statusEmailDefaultPreview?.subject || '');
          setStatusEmailMessage(statusEmailDefaultPreview?.message || '');
        }}
        onSubjectChange={setStatusEmailSubject}
        onMessageChange={setStatusEmailMessage}
      />

      </div>
    </div>
  );
};

export default OrderDetail;
